import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Film,
  Clapperboard, Plus, Loader2, Trash2, Sparkles, FileText, Wand2, ChevronLeft, Copy, Check,
  Download, ExternalLink, AlertTriangle, RefreshCw, Headphones, Video, ScrollText, Mic, Rss, Image as ImageIcon,
  ChevronDown, ChevronRight, Layers, Gauge, FolderOpen, Eye, Globe,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useProjects } from '../hooks/useRag'
import Markdown from '../components/Markdown'
import { SlidePreview } from '../components/SlidePreview'
import * as api from '../services/overviewsApi'
import OverviewAutopilot, { PublishingCard } from '../components/OverviewAutopilot'

// Overviews (doc/plan-overviews.md, O1): plan a series of audio/video digests
// over a project's sources, brief each episode from the corpus (grounded, with
// citations), and hand the prompt pack + source pack to NotebookLM. The asset
// round-trip (O2) and YouTube publication (O3) attach to the same episode rows.

const STAGES = [
  ['planned', 'planned'],
  ['briefed', 'brief'],
  ['prompted', 'prompts'],
  ['asset_ready', 'asset'],
  ['published', 'published'],
  ['indexed', 'indexed'],
]
const ORDER = ['planned', 'briefing', 'briefed', 'prompting', 'prompted', 'scripting', 'scripted', 'generating', 'awaiting_asset', 'processing', 'asset_ready', 'publishing', 'published', 'indexed']
const rank = (s) => (s === 'failed' ? -1 : ORDER.indexOf(s))
const BUSY = new Set(['briefing', 'prompting', 'scripting', 'generating', 'processing', 'publishing'])
const BUSY_TEXT = (ep, series) => {
  const p = ep.progress
  switch (ep.status) {
    case 'briefing':
      if (p?.step === 'coverage') return 'Checking what the corpus holds for each topic…'
      if (p?.step === 'evidence') return 'Weighing key claims with the Truth lens…'
      if (p?.step === 'gaps') return 'Auditing the brief for gaps in the sources…'
      if (p?.step === 'assemble') return 'Assembling the brief and the source pack…'
      return ep.kind === 'deepdive'
      ? `Deep brief: researching each of the ${ep.topics.length} sub-topics (3 asks + merge each)… ${p?.done ?? 0}/${ep.topics.length}`
      : `Asking the corpus about each of the ${ep.topics.length} topics… ${p?.done ?? 0}/${ep.topics.length}`
    case 'prompting': return 'Composing the prompt pack…'
    case 'scripting': return 'Writing the two-host script from the brief…'
    case 'generating':
      if (p?.step === 'synthesize') return `Voicing the dialogue… ${p.done ?? 0}/${p.total ?? '?'} turns`
      if (p?.step === 'assemble') return 'Mixing the track, writing captions and chapters…'
      if (p?.step === 'render') return series?.format === 'video' ? 'Rendering the slides video…' : 'Rendering the cover and audiogram…'
      return 'Generating…'
    case 'processing': return 'Transcribing the overview, placing chapters, rendering the cover and audiogram…'
    case 'publishing':
      if (p?.step === 'publish') return 'Adding the episode to the podcast feed…'
      if (p?.step === 'index-back') return 'Indexing the overview back into the project…'
      return 'Uploading to YouTube, setting the thumbnail, playlist and captions…'
    default: return p?.step === 'imagine' ? 'Generating the AI thumbnail…' : 'Working…'
  }
}

const input =
  'h-10 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist'
const btn =
  'inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed dark:text-gold'
const btnGhost =
  'inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm text-ink-soft hover:bg-parchment-200 disabled:opacity-40 dark:text-mist/70 dark:hover:bg-midnight-700'
const card = 'rounded-xl border border-parchment-border bg-white/60 dark:border-midnight-600 dark:bg-midnight-800/50'

function StatusChip({ status }) {
  const failed = status === 'failed'
  const busy = BUSY.has(status)
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${
        failed ? 'bg-red-500/15 text-red-600' : busy ? 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300' : 'bg-gold/15 text-gold-deep dark:text-gold'
      }`}
    >
      {busy && <Loader2 className="w-3 h-3 animate-spin" />}
      {status.replace('_', ' ')}
    </span>
  )
}

function StageTrack({ status }) {
  const r = rank(status)
  return (
    <div className="flex items-center gap-1">
      {STAGES.map(([key, label]) => {
        const done = r >= ORDER.indexOf(key)
        return (
          <span
            key={key}
            title={label}
            className={`h-1.5 w-6 rounded-full ${done ? 'bg-gradient-to-r from-gold to-spark' : 'bg-parchment-border dark:bg-midnight-600'}`}
          />
        )
      })}
    </div>
  )
}

function CopyButton({ text, label = 'Copy' }) {
  const [ok, setOk] = useState(false)
  return (
    <button
      type="button"
      className={btnGhost}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text || '')
          setOk(true)
          setTimeout(() => setOk(false), 1500)
        } catch {}
      }}
      disabled={!text}
    >
      {ok ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {ok ? 'Copied' : label}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

// A card whose body folds under its header, so a long episode page reads as a list
// of headings on a phone. Small screens start folded, wide ones open; a toggle is
// remembered per card kind and screen class in this browser. A button in the header
// (Check, Edit, Copy…) opens the card so its result shows.
const narrowScreen = () => { try { return window.matchMedia('(max-width: 639px)').matches } catch { return false } }
const foldKey = (id) => `veralis.overviews.fold.${narrowScreen() ? 'm' : 'd'}.${id}`
function readFold(id, defaultOpen) {
  try {
    const v = localStorage.getItem(foldKey(id))
    if (v === '1') return true
    if (v === '0') return false
  } catch { /* storage blocked */ }
  return defaultOpen ?? !narrowScreen()
}
function Fold({ id, head, body = 'space-y-3', className = '', defaultOpen, forceOpen = false, children }) {
  const [open, setOpen] = useState(() => readFold(id, defaultOpen))
  const shown = open || forceOpen
  const toggle = (e) => {
    e?.stopPropagation()
    const next = !shown
    setOpen(next)
    try { localStorage.setItem(foldKey(id), next ? '1' : '0') } catch { /* storage blocked */ }
  }
  const onHead = (e) => {
    if (e.target.closest('button,a,input,select,textarea,label')) { if (!shown) setOpen(true) } else toggle()
  }
  return (
    <section className={`${card} p-4 ${className}`}>
      <div className="flex items-start gap-2 cursor-pointer" onClick={onHead}>
        <button type="button" aria-expanded={shown} onClick={toggle} title={shown ? 'Collapse' : 'Expand'}
          className="mt-0.5 shrink-0 rounded text-ink-soft hover:text-ink dark:text-mist/60 dark:hover:text-mist">
          {shown ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0">{head}</div>
      </div>
      {shown && <div className={`mt-3 ${body}`}>{children}</div>}
    </section>
  )
}

export default function OverviewsPage() {
  const [seriesId, setSeriesId] = useState(null)
  const [episodeId, setEpisodeId] = useState(null)
  const [wizard, setWizard] = useState(false)

  return (
    <div className="space-y-6">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold-deep dark:text-gold mb-2">Illuminating Truth</div>
        <h1 className="font-display text-4xl text-ink dark:text-mist">Overviews</h1>
        <p className="mt-2 text-ink-soft dark:text-mist/60">
          Turn a project's sources into an audio or video series: plan the topics, brief each episode from the
          corpus, hand the prompts to NotebookLM, publish.
        </p>
      </header>

      {episodeId ? (
        <EpisodeDetail id={episodeId} onBack={() => setEpisodeId(null)} onOpenEpisode={setEpisodeId} />
      ) : seriesId ? (
        <SeriesDetail id={seriesId} onBack={() => setSeriesId(null)} onOpenEpisode={setEpisodeId} />
      ) : wizard ? (
        <SeriesWizard onDone={(s) => { setWizard(false); if (s) setSeriesId(s.id) }} />
      ) : (
        <SeriesList onOpen={setSeriesId} onNew={() => setWizard(true)} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Series list
// ---------------------------------------------------------------------------

function SeriesList({ onOpen, onNew }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['overviews', 'series'], queryFn: api.listSeries, refetchInterval: 15000 })
  const del = useMutation({ mutationFn: api.deleteSeries, onSuccess: () => qc.invalidateQueries({ queryKey: ['overviews'] }) })
  const series = q.data?.series ?? []
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink-soft dark:text-mist/60">{series.length} series</p>
        <button className={btn} onClick={onNew}><Plus className="w-4 h-4" /> New series</button>
      </div>
      {q.isError && <p className="text-sm text-red-600">{q.error?.message}</p>}
      {!q.isLoading && series.length === 0 && (
        <div className="rounded-xl border border-dashed border-parchment-border p-8 text-center text-ink-soft dark:border-midnight-600 dark:text-mist/50">
          <Clapperboard className="w-8 h-8 mx-auto mb-2 opacity-50" />
          No series yet. Start one from a project and let the planner propose the episodes.
        </div>
      )}
      {series.map((s) => {
        const c = s.episode_counts ?? {}
        const done = (c.published ?? 0) + (c.indexed ?? 0)
        return (
          <div key={s.id} className={`${card} px-4 py-3 flex items-center gap-3`}>
            {s.format === 'video' ? <Video className="w-4 h-4 text-gold-deep dark:text-gold" /> : <Headphones className="w-4 h-4 text-gold-deep dark:text-gold" />}
            <button className="flex-1 min-w-0 text-left" onClick={() => onOpen(s.id)}>
              <div className="text-sm text-ink dark:text-mist truncate">{s.title}</div>
              <div className="text-[11px] text-ink-soft dark:text-mist/50">
                {s.projects.join(', ')} · {s.language} · {s.channel} · {s.generator}
                {s.episodes_total ? ` · ${done}/${s.episodes_total} published` : s.plan_status ? ` · plan ${s.plan_status}` : ''}
                {s.cost_usd > 0 ? ` · ≈ ${fmtUsd(s.cost_usd)}` : ''}
              </div>
            </button>
            <button className="p-1.5 rounded text-ink-soft hover:text-red-600" title="Delete series" onClick={() => { if (confirm(`Delete series "${s.title}" and its episodes?`)) del.mutate(s.id) }}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Wizard
// ---------------------------------------------------------------------------

function SeriesWizard({ onDone }) {
  const qc = useQueryClient()
  const projectsQ = useProjects()
  const caps = useCapabilities()
  const [form, setForm] = useState({
    title: '', projects: [], language: 'cs', format: 'audio', channel: 'youtube', generator: 'handoff',
    episodes_target: '', audience: '', tone: '', length: '',
  })
  const [touched, setTouched] = useState(false)
  // Settings → Overviews decide what a new series starts as; applied until the
  // owner changes something here (an explicit choice always wins)
  useEffect(() => {
    const d = caps.data?.defaults
    if (!d || touched) return
    setForm((f) => ({
      ...f,
      language: d.language || f.language, format: d.format || f.format,
      channel: d.channel || f.channel, generator: d.generator || f.generator,
    }))
  }, [caps.data]) // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => { setTouched(true); setForm((f) => ({ ...f, [k]: v })) }
  const create = useMutation({
    mutationFn: api.createSeries,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ['overviews'] }); onDone(d.series) },
  })
  const toggleProject = (name) =>
    set('projects', form.projects.includes(name) ? form.projects.filter((p) => p !== name) : [...form.projects, name])
  const submit = () =>
    create.mutate({
      title: form.title.trim(), projects: form.projects, language: form.language, format: form.format,
      channel: form.channel, generator: form.generator,
      episodes_target: form.episodes_target ? Number(form.episodes_target) : null,
      style: { audience: form.audience || null, tone: form.tone || null, length: form.length || null },
    })

  return (
    <div className={`${card} p-5 space-y-4`}>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl text-ink dark:text-mist">New series</h2>
        <button className={btnGhost} onClick={() => onDone(null)}><ChevronLeft className="w-4 h-4" /> Back</button>
      </div>
      <label className="block space-y-1">
        <span className="text-xs text-ink-soft dark:text-mist/60">Title</span>
        <input className={`${input} w-full`} value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Co mění novely stavebního zákona" />
      </label>
      <div className="space-y-1">
        <span className="text-xs text-ink-soft dark:text-mist/60">Projects (sources)</span>
        <div className="flex flex-wrap gap-1.5">
          {(projectsQ.data?.projects ?? []).map((p) => (
            <button
              key={p.name}
              type="button"
              onClick={() => toggleProject(p.name)}
              className={`px-2.5 py-1 rounded-full text-xs border ${
                form.projects.includes(p.name)
                  ? 'bg-gold/15 border-gold/50 text-gold-deep dark:text-gold'
                  : 'border-parchment-border text-ink-soft dark:border-midnight-600 dark:text-mist/60'
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft dark:text-mist/60">Format</span>
          <select className={`${input} w-full`} value={form.format} onChange={(e) => set('format', e.target.value)}>
            <option value="audio">Audio overview</option>
            <option value="video">Video overview</option>
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft dark:text-mist/60">Channel</span>
          <select className={`${input} w-full`} value={form.channel} onChange={(e) => set('channel', e.target.value)}>
            <option value="youtube">YouTube</option>
            <option value="podcast">Podcast (RSS feed)</option>
            <option value="manual">Manual bundle</option>
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft dark:text-mist/60">Generator</span>
          <select className={`${input} w-full`} value={form.generator} onChange={(e) => set('generator', e.target.value)}>
            <option value="handoff">NotebookLM (handoff)</option>
            <option value="native">Native (TTS / slides)</option>
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft dark:text-mist/60">Language</span>
          <select className={`${input} w-full`} value={form.language} onChange={(e) => set('language', e.target.value)}>
            {['cs', 'en', 'sk', 'de', 'ru', 'pl'].map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft dark:text-mist/60">Episodes (blank = planner decides)</span>
          <input className={`${input} w-full`} type="number" min="1" max="12" value={form.episodes_target} onChange={(e) => set('episodes_target', e.target.value)} />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft dark:text-mist/60">Audience</span>
          <input className={`${input} w-full`} value={form.audience} onChange={(e) => set('audience', e.target.value)} placeholder="e.g. stavebníci a projektanti" />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft dark:text-mist/60">Tone</span>
          <input className={`${input} w-full`} value={form.tone} onChange={(e) => set('tone', e.target.value)} placeholder="e.g. věcný, přístupný" />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-ink-soft dark:text-mist/60">Target length</span>
          <input className={`${input} w-full`} value={form.length} onChange={(e) => set('length', e.target.value)} placeholder="e.g. 15 minut" />
        </label>
      </div>
      {create.isError && <p className="text-sm text-red-600">{create.error?.message}</p>}
      <div className="flex justify-end">
        <button className={btn} disabled={!form.title.trim() || !form.projects.length || create.isPending} onClick={submit}>
          {create.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create series
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Series detail: plan proposal + episode board
// ---------------------------------------------------------------------------

function SeriesDetail({ id, onBack, onOpenEpisode }) {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['overviews', 'series', id],
    queryFn: () => api.getSeries(id),
    refetchInterval: (query) => {
      const d = query.state.data
      const busy = d?.series?.plan_busy || (d?.episodes ?? []).some((e) => e.busy)
      return busy ? 3000 : 20000
    },
  })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['overviews'] })
  const propose = useMutation({ mutationFn: () => api.proposePlan(id), onSuccess: invalidate })
  const brief = useMutation({ mutationFn: api.briefEpisode, onSuccess: invalidate })
  const prompts = useMutation({ mutationFn: api.promptEpisode, onSuccess: invalidate })
  const delEp = useMutation({ mutationFn: api.deleteEpisode, onSuccess: invalidate })
  const playlist = useMutation({ mutationFn: (v) => api.patchSeries({ id, deepdive_playlist: v }), onSuccess: invalidate })
  // parents whose deep dives are folded away on the board
  const [collapsed, setCollapsed] = useState(() => new Set())

  const series = q.data?.series
  const episodes = q.data?.episodes ?? []
  if (!series) return q.isError ? <p className="text-sm text-red-600">{q.error?.message}</p> : <Loader2 className="w-5 h-5 animate-spin" />

  const showPlan = series.plan && series.plan_status !== 'applied'
  const err = propose.error?.message || brief.error?.message || prompts.error?.message

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <button className={btnGhost} onClick={onBack}><ChevronLeft className="w-4 h-4" /> Series</button>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-2xl text-ink dark:text-mist truncate">{series.title}</h2>
          <p className="text-xs text-ink-soft dark:text-mist/50">
            {series.projects.join(', ')} · {series.format} · {series.language} · {series.channel} · {series.generator}
          </p>
          {series.costs?.total > 0 && (
            <p className="text-[11px] text-ink-soft dark:text-mist/50">
              Spent ≈ {fmtUsd(series.costs.total)} · {kindLine(series.costs.kinds)}
              {series.costs.series_level > 0 ? ` · series plan ${fmtUsd(series.costs.series_level)}` : ''}
              {series.costs.deleted_episodes > 0 ? ` · deleted episodes ${fmtUsd(series.costs.deleted_episodes)}` : ''}
              {series.costs.estimated ? ' · partly estimated' : ''}
            </p>
          )}
          {episodes.some((e) => e.kind === 'deepdive') && (
            <label className="mt-1 inline-flex items-center gap-2 text-[11px] text-ink-soft dark:text-mist/50">
              Deep dives on YouTube:
              <select className="h-7 rounded-md px-2 text-[11px] bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist" value={series.deepdive_playlist ?? ''} disabled={playlist.isPending} onChange={(e) => playlist.mutate(e.target.value || null)}>
                <option value="">Settings default</option>
                <option value="same">same playlist as the series</option>
                <option value="separate">separate “· Deep dives” playlist</option>
              </select>
            </label>
          )}
        </div>
        <button className={btn} onClick={() => propose.mutate()} disabled={series.plan_busy || propose.isPending}>
          {series.plan_busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {series.plan ? 'Re-propose plan' : 'Propose plan'}
        </button>
      </div>
      {err && <p className="text-sm text-red-600">{err}</p>}
      {series.plan_status === 'failed' && (
        <p className="text-sm text-red-600 inline-flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Plan failed: {series.plan_error}</p>
      )}
      {series.plan_busy && (
        <p className="text-sm text-ink-soft dark:text-mist/60 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Reading the sources and proposing topics… (one LLM pass over the project digest)
        </p>
      )}

      {showPlan && <PlanEditor series={series} onApplied={invalidate} />}

      <ScopeCard
        seriesId={series.id}
        scope={{ projects: series.projects, include_docs: series.scope?.include_docs ?? [], exclude_docs: series.scope?.exclude_docs ?? [], source: 'series' }}
        note="home projects — finished episodes are indexed back here; an episode can widen or narrow this on its Brief tab"
        onSave={(d) => api.patchSeries({ id: series.id, projects: d.projects, scope: { include_docs: d.include_docs, exclude_docs: d.exclude_docs } }).then(invalidate)}
      />

      <PublishingCard key={`pub-${series.id}-${series.updated_at}`} series={series} onSaved={invalidate} />

      {episodes.length > 0 && <OverviewAutopilot series={series} onOpenEpisode={onOpenEpisode} onChanged={invalidate} />}

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm uppercase tracking-[0.18em] text-gold-deep dark:text-gold">Episodes</h3>
          {series.plan_status === 'applied' && (
            <span className="text-[11px] text-ink-soft dark:text-mist/50">plan applied · re-propose to regroup</span>
          )}
        </div>
        {episodes.length === 0 && !showPlan && (
          <div className="rounded-xl border border-dashed border-parchment-border p-6 text-center text-ink-soft dark:border-midnight-600 dark:text-mist/50">
            No episodes yet — propose a plan, edit it, then create the episodes.
          </div>
        )}
        {episodes.map((e) => {
          const r = rank(e.status)
          // gate on what the episode HAS, not on the status rank: a failed step
          // ranks -1 and must not lock the next step when its input exists
          const canBrief = !e.busy
          const canPrompt = !e.busy && !!e.has_brief
          const deep = e.kind === 'deepdive'
          if (deep && collapsed.has(e.parent_id)) return null
          const folded = collapsed.has(e.id)
          const toggle = () => setCollapsed((s) => { const n = new Set(s); if (n.has(e.id)) n.delete(e.id); else n.add(e.id); return n })
          return (
            <div key={e.id} className={`${card} px-4 py-3 space-y-1.5${deep ? ' ml-8 border-l-2 border-l-gold/40' : ''}`}>
              <div className="flex items-center gap-3">
                {!deep && e.children > 0 ? (
                  <button className="w-5 text-ink-soft dark:text-mist/50" onClick={toggle} title={folded ? `Show ${e.children} deep dives` : 'Hide the deep dives'}>
                    {folded ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                ) : <span className="w-5" />}
                <span className="text-[11px] text-ink-soft dark:text-mist/50 w-8">#{e.label ?? e.ordinal}</span>
                <button className="flex-1 min-w-0 text-left" onClick={() => onOpenEpisode(e.id)}>
                  <div className="text-sm text-ink dark:text-mist truncate">
                    {deep && <span className="mr-2 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-gold/15 text-gold-deep dark:text-gold">Deep dive</span>}
                    {e.title}
                  </div>
                  <div className="text-[11px] text-ink-soft dark:text-mist/50">
                    {deep ? `${e.topics.length} sub-topics` : `${e.topics.length} topics${e.children ? ` · ${e.children} deep dive${e.children > 1 ? 's' : ''}` : ''}`}
                    {e.prompt_pack?.youtube?.title ? ` · YT: ${e.prompt_pack.youtube.title}` : ''}
                    {e.cost_usd > 0 ? ` · ≈ ${fmtUsd(e.cost_usd)}` : ''}
                    {e.coverage?.weak?.length > 0 && <span className="text-amber-700 dark:text-amber-400"> · {e.coverage.weak.length} weak coverage</span>}
                    {e.scope_source && e.scope_source !== 'series' && <span title={`source scope ${e.scope_source === 'episode' ? 'of its own' : 'inherited from the parent'}`}> · own scope</span>}
                  </div>
                </button>
                <StatusChip status={e.status} />
                <button className={btnGhost} disabled={!canBrief || brief.isPending} onClick={async () => { if (await confirmBrief(e)) brief.mutate(e.id) }} title={e.kind === 'deepdive' ? 'Deep brief: 3 asks per sub-topic, merged, optional truth lens' : 'Brief the episode from the corpus (one ask per topic)'}>
                  <FileText className="w-3.5 h-3.5" /> {e.has_brief ? 'Re-brief' : 'Brief'}
                </button>
                <button className={btnGhost} disabled={!canPrompt || prompts.isPending} onClick={() => prompts.mutate(e.id)} title="Compose the NotebookLM prompts + YouTube metadata">
                  <Wand2 className="w-3.5 h-3.5" /> {e.prompt_pack?.youtube?.title ? 'Re-prompt' : 'Prompts'}
                </button>
                {series.generator === 'native' && (
                  <button className={btnGhost} disabled={!canPrompt} onClick={() => onOpenEpisode(e.id)} title="Script + native generation (TTS dialogue, slides)">
                    <ScrollText className="w-3.5 h-3.5" /> Script
                  </button>
                )}
                <button className="p-1.5 rounded text-ink-soft hover:text-red-600" disabled={e.busy} title="Delete episode" onClick={() => { if (confirm(`Delete episode "${e.title}"?`)) delEp.mutate(e.id) }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex items-center gap-3 pl-9">
                <StageTrack status={e.status} />
                {e.last_error && e.status === 'failed' && <span className="text-[11px] text-red-600 truncate" title={e.last_error}>{e.last_error}</span>}
              </div>
            </div>
          )
        })}
      </section>
    </div>
  )
}

function PlanEditor({ series, onApplied }) {
  const qc = useQueryClient()
  const [topics, setTopics] = useState(series.plan.topics ?? [])
  const [episodes, setEpisodes] = useState(series.plan.episodes ?? [])
  useEffect(() => { setTopics(series.plan.topics ?? []); setEpisodes(series.plan.episodes ?? []) }, [series.plan?.proposed_at, series.plan?.edited_at]) // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({ mutationFn: () => api.savePlan({ id: series.id, topics, episodes }), onSuccess: () => qc.invalidateQueries({ queryKey: ['overviews'] }) })
  const apply = useMutation({
    mutationFn: () => api.applyPlan({ id: series.id, plan: { ...series.plan, topics, episodes }, replace: true }),
    onSuccess: onApplied,
  })
  const setEp = (i, patch) => setEpisodes((es) => es.map((e, j) => (j === i ? { ...e, ...patch } : e)))
  const toggleTopic = (i, t) =>
    setEp(i, { topic_indexes: episodes[i].topic_indexes.includes(t) ? episodes[i].topic_indexes.filter((x) => x !== t) : [...episodes[i].topic_indexes, t].sort((a, b) => a - b) })
  const removeTopic = (t) => {
    setTopics((ts) => ts.filter((_, i) => i !== t))
    setEpisodes((es) => es.map((e) => ({ ...e, topic_indexes: e.topic_indexes.filter((x) => x !== t).map((x) => (x > t ? x - 1 : x)) })))
  }
  // coverage of the (edited) topic list before any episode exists — weak ones
  // are better merged or removed here than briefed
  const coverage = useMutation({ mutationFn: () => api.planCoverage({ id: series.id, topics }), onSuccess: () => qc.invalidateQueries({ queryKey: ['overviews'] }) })
  const covByTitle = new Map((series.plan.coverage?.topics ?? []).map((t) => [t.title, t]))
  const weakPlan = topics.filter((t) => ['weak', 'none'].includes(covByTitle.get(t.title)?.grade))

  return (
    <section className={`${card} p-4 space-y-4`}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm uppercase tracking-[0.18em] text-gold-deep dark:text-gold">Proposed plan</h3>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-ink-soft dark:text-mist/50">{series.plan.documents} documents read · edit freely, then create the episodes</span>
          <button className={btnGhost} disabled={coverage.isPending || !topics.length} onClick={() => coverage.mutate()} title="One semantic search per topic (no LLM): is there enough material?">
            {coverage.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gauge className="w-3.5 h-3.5" />} {series.plan.coverage ? 'Re-check coverage' : 'Check coverage'}
          </button>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="text-xs text-ink-soft dark:text-mist/60">Topics</div>
        {topics.map((t, i) => {
          const cv = covByTitle.get(t.title)
          return (
          <div key={i} className="flex items-start gap-2">
            <span className="text-[11px] text-ink-soft dark:text-mist/50 w-9 pt-2.5 inline-flex items-center gap-1.5" title={cv ? `${GRADE_TEXT[cv.grade]} (${cv.close} close passages, ${cv.documents} docs)` : 'not checked'}>
              {cv && <span className={`w-2 h-2 rounded-full shrink-0 ${GRADE_DOT[cv.grade] || GRADE_DOT.none}`} />}{i + 1}
            </span>
            <input className={`${input} w-1/3`} value={t.title} onChange={(e) => setTopics((ts) => ts.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
            <input className={`${input} flex-1`} value={t.description ?? ''} onChange={(e) => setTopics((ts) => ts.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} />
            <button className="p-2 text-ink-soft hover:text-red-600" onClick={() => removeTopic(i)} title="Remove topic"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
          )
        })}
        <button className={btnGhost} onClick={() => setTopics((ts) => [...ts, { title: 'New topic', description: '' }])}><Plus className="w-3.5 h-3.5" /> Add topic</button>
        {coverage.isError && <p className="text-sm text-red-600">{coverage.error?.message}</p>}
        {weakPlan.length > 0 && (
          <p className="text-xs text-amber-700 dark:text-amber-400 inline-flex items-start gap-1.5">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{weakPlan.length} of {topics.length} topics have weak coverage ({weakPlan.map((t) => t.title).join(', ')}). Merge them into a neighbour or remove them — a brief would mostly say the sources are silent.</span>
          </p>
        )}
      </div>
      <div className="space-y-2">
        <div className="text-xs text-ink-soft dark:text-mist/60">Episodes</div>
        {episodes.map((e, i) => (
          <div key={i} className="rounded-lg border border-parchment-border dark:border-midnight-600 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-ink-soft dark:text-mist/50">#{i + 1}</span>
              <input className={`${input} flex-1`} value={e.title} onChange={(ev) => setEp(i, { title: ev.target.value })} />
              <button className="p-2 text-ink-soft hover:text-red-600" onClick={() => setEpisodes((es) => es.filter((_, j) => j !== i))} title="Remove episode"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <input className={`${input} w-full`} value={e.angle ?? ''} placeholder="Angle / framing (optional)" onChange={(ev) => setEp(i, { angle: ev.target.value })} />
            <div className="flex flex-wrap gap-1.5">
              {topics.map((t, ti) => (
                <button
                  key={ti}
                  type="button"
                  onClick={() => toggleTopic(i, ti)}
                  className={`px-2 py-0.5 rounded-full text-[11px] border ${
                    e.topic_indexes.includes(ti)
                      ? 'bg-gold/15 border-gold/50 text-gold-deep dark:text-gold'
                      : 'border-parchment-border text-ink-soft dark:border-midnight-600 dark:text-mist/60'
                  }`}
                >
                  {ti + 1}. {t.title}
                </button>
              ))}
            </div>
          </div>
        ))}
        <button className={btnGhost} onClick={() => setEpisodes((es) => [...es, { title: `Episode ${es.length + 1}`, topic_indexes: [], angle: '' }])}><Plus className="w-3.5 h-3.5" /> Add episode</button>
      </div>
      {(save.isError || apply.isError) && <p className="text-sm text-red-600">{save.error?.message || apply.error?.message}</p>}
      <div className="flex justify-end gap-2">
        <button className={btnGhost} onClick={() => save.mutate()} disabled={save.isPending}>Save edits</button>
        <button className={btn} onClick={() => apply.mutate()} disabled={apply.isPending || !episodes.some((e) => e.topic_indexes.length)}>
          {apply.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Create episodes
        </button>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Before spending a brief: warn about topics the coverage check graded weak
// (the brief will mostly say the sources are silent there) and show a deep
// dive's estimate (3 asks per sub-topic + merges + truth lens).
async function confirmBrief(ep) {
  const parts = []
  const weak = ep?.coverage?.weak ?? []
  if (weak.length) {
    parts.push(`Weak source coverage for ${weak.length} of ${ep.coverage.topics.length} topics: ${weak.join(', ')}.\n` +
      'The brief will likely say the sources are silent there — consider merging or dropping them, or adding sources first.')
  }
  if (ep?.kind === 'deepdive') {
    try {
      const { estimate: e } = await api.briefEstimate(ep.id)
      const tl = e.truth_lens_claims ? ` + truth lens on ${e.truth_lens_claims} claims` : ''
      parts.push(`Deep brief over ${e.items} sub-topics: ${e.asks} asks + ${e.llm_tasks} LLM calls${tl} ≈ $${e.usd.toFixed(2)} (${e.basis}).`)
    } catch {
      parts.push('Deep brief: 3 asks per sub-topic plus merges.')
    }
  }
  if (!parts.length) return true
  return window.confirm(`${parts.join('\n\n')}\n\nContinue?`)
}

const GRADE_DOT = { strong: 'bg-emerald-500', fair: 'bg-amber-400', weak: 'bg-red-500', none: 'bg-gray-400' }
const GRADE_TEXT = {
  strong: 'strong — several close passages across the sources',
  fair: 'fair — a couple of close passages',
  weak: 'weak — only distant matches; the brief will mostly say the sources are silent',
  none: 'none — nothing retrieved',
}
const discoverHref = (q) => `/research?discover=${encodeURIComponent(q || '')}`

// Coverage check: one LLM-free search per topic — what the corpus holds
// before the brief spends asks on it. The brief saga refreshes it as well.
function CoverageCard({ ep, scope, onChanged }) {
  const check = useMutation({ mutationFn: () => api.checkCoverage(ep.id), onSuccess: onChanged })
  const cov = ep.coverage
  const items = ep.kind === 'deepdive' ? (ep.focus?.subtopics?.length ? ep.focus.subtopics : ep.topics) : ep.topics
  if (!items?.length) return null
  const weak = cov?.weak ?? []
  const key = (x) => JSON.stringify([x.projects, x.include_docs ?? [], x.exclude_docs ?? []])
  const stale = !!(cov?.scope && scope && key(cov.scope) !== key(scope))
  return (
    <Fold id="coverage" head={
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm uppercase tracking-[0.18em] text-gold-deep dark:text-gold inline-flex items-center gap-1.5"><Gauge className="w-4 h-4" /> Source coverage</h3>
          <p className="text-[11px] text-ink-soft dark:text-mist/50">
            {cov
              ? `Checked ${new Date(cov.checked_at).toLocaleString()} · ${cov.summary.strong} strong · ${cov.summary.fair} fair · ${cov.summary.weak + cov.summary.none} weak`
              : 'One semantic search per topic, no LLM — see what the corpus holds before briefing.'}
          </p>
        </div>
        <button className={btnGhost} disabled={check.isPending || ep.busy} onClick={() => check.mutate()}>
          {check.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} {cov ? 'Re-check' : 'Check coverage'}
        </button>
      </div>}>
      {check.isError && <p className="text-sm text-red-600">{check.error?.message}</p>}
      {stale && <p className="text-xs text-amber-700 dark:text-amber-400">The source scope changed since this check — re-check.</p>}
      {cov && (
        <ul className="space-y-1">
          {cov.topics.map((t, i) => (
            <li key={i} className="flex items-start gap-2 text-xs" title={GRADE_TEXT[t.grade]}>
              <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${GRADE_DOT[t.grade] || GRADE_DOT.none}`} />
              <span className="flex-1 min-w-0">
                <span className="text-ink dark:text-mist">{t.title}</span>
                <span className="text-ink-soft dark:text-mist/50"> · {t.close} close passage{t.close === 1 ? '' : 's'} in {t.documents} document{t.documents === 1 ? '' : 's'}{t.best != null ? ` · nearest ${t.best}` : ''}</span>
                {t.top_sources?.length > 0 && (
                  <span className="block text-[11px] text-ink-soft dark:text-mist/40 truncate">{t.top_sources.map((s) => `${s.name} (${s.chunks})`).join(' · ')}</span>
                )}
              </span>
              {(t.grade === 'weak' || t.grade === 'none') && (
                <Link className="underline text-[11px] shrink-0" to={discoverHref(t.query)} title="Find new sources for this topic in Research → Discover">Find sources →</Link>
              )}
            </li>
          ))}
        </ul>
      )}
      {weak.length > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-400 inline-flex items-start gap-1.5">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{weak.length} of {cov.topics.length} topics have weak coverage. The brief will largely say the sources are silent there — merge or drop them, or add sources first.</span>
        </p>
      )}
    </Fold>
  )
}

// Gap audit (the brief saga's last LLM call): what the sources could not
// answer, what rests on a single source; each gap seeds Research → Discover.
// Source scope: which projects and documents retrieval uses. The series'
// projects are home (finished episodes are indexed back there); an episode may
// widen the projects (a deep dive going beyond the series) or leave documents
// out without touching the series or its siblings. A deep dive inherits its
// parent's own scope, then the series'. Unchecked document = left out.
function ScopeCard({ seriesId, scope, note, onSave, onReset }) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(null)
  const [filter, setFilter] = useState('')
  const [err, setErr] = useState(null)
  const [saving, setSaving] = useState(false)
  const projectsQ = useProjects()
  const cur = draft ?? scope
  const docsQ = useQuery({
    queryKey: ['overviews', 'scope-docs', seriesId, cur.projects.join('|')],
    queryFn: () => api.seriesDocuments(seriesId, cur.projects),
    enabled: open,
  })
  const begin = () => {
    setDraft({ projects: [...scope.projects], include_docs: [...(scope.include_docs ?? [])], exclude_docs: [...(scope.exclude_docs ?? [])] })
    setOpen(true)
  }
  const toggleProject = (name) => setDraft((d) => {
    const has = d.projects.includes(name)
    if (has && d.projects.length === 1) return d // keep at least one
    return { ...d, projects: has ? d.projects.filter((p) => p !== name) : [...d.projects, name] }
  })
  const hit = (list, doc) => list.includes(doc.source) || list.includes(doc.name)
  const excluded = (doc) => hit(cur.exclude_docs ?? [], doc) || ((cur.include_docs?.length ?? 0) > 0 && !hit(cur.include_docs, doc))
  const toggleDoc = (doc) => setDraft((d) => ({
    ...d,
    exclude_docs: hit(d.exclude_docs, doc) ? d.exclude_docs.filter((x) => x !== doc.source && x !== doc.name) : [...d.exclude_docs, doc.source],
  }))
  const act = async (fn) => {
    setSaving(true); setErr(null)
    try { await fn(); setDraft(null); setOpen(false) } catch (e) { setErr(e?.message || String(e)) } finally { setSaving(false) }
  }
  const docs = (docsQ.data?.documents ?? []).filter((d) => !filter || d.name.toLowerCase().includes(filter.toLowerCase()))
  const n = (x) => `${x} document${x === 1 ? '' : 's'}`
  return (
    <Fold id="scope" forceOpen={!!draft} head={
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm uppercase tracking-[0.18em] text-gold-deep dark:text-gold inline-flex items-center gap-1.5"><FolderOpen className="w-4 h-4" /> Source scope</h3>
          <p className="text-[11px] text-ink-soft dark:text-mist/50 truncate" title={scope.projects.join(', ')}>
            {scope.projects.join(', ')}
            {scope.include_docs?.length ? ` · only ${n(scope.include_docs.length)}` : ''}
            {scope.exclude_docs?.length ? ` · ${n(scope.exclude_docs.length)} left out` : ''}
            {note ? ` · ${note}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onReset && !draft && <button className={btnGhost} disabled={saving} onClick={() => act(onReset)} title="Drop this episode's own scope and inherit again">Reset</button>}
          {draft ? (
            <>
              <button className={btnGhost} disabled={saving} onClick={() => { setDraft(null); setOpen(false); setErr(null) }}>Cancel</button>
              <button className={btn} disabled={saving} onClick={() => act(() => onSave(draft))}>
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
              </button>
            </>
          ) : (
            <button className={btnGhost} onClick={begin}>Edit</button>
          )}
        </div>
      </div>}>
      {err && <p className="text-sm text-red-600">{err}</p>}
      {draft && (
        <>
          <div className="flex flex-wrap gap-1.5">
            {(projectsQ.data?.projects ?? []).map((p) => (
              <button
                key={p.name}
                type="button"
                onClick={() => toggleProject(p.name)}
                title={`${p.documents_count ?? '?'} documents`}
                className={`px-2 py-0.5 rounded-full text-[11px] border ${
                  draft.projects.includes(p.name)
                    ? 'bg-gold/15 border-gold/50 text-gold-deep dark:text-gold'
                    : 'border-parchment-border text-ink-soft dark:border-midnight-600 dark:text-mist/60'
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
          <input className={`${input} w-full`} placeholder="Filter documents…" value={filter} onChange={(e) => setFilter(e.target.value)} />
          {docsQ.isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
          {docsQ.isError && <p className="text-sm text-red-600">{docsQ.error?.message}</p>}
          <ul className="max-h-72 overflow-auto space-y-0.5 text-xs">
            {docs.map((d) => (
              <li key={`${d.project}:${d.source}`} className="flex items-center gap-2">
                <input type="checkbox" className="accent-gold" checked={!d.curated_excluded && !excluded(d)} disabled={d.curated_excluded} onChange={() => toggleDoc(d)} />
                <span className={`flex-1 min-w-0 truncate ${d.curated_excluded ? 'line-through text-ink-soft/60 dark:text-mist/30' : 'text-ink dark:text-mist'}`} title={d.source}>{d.name}</span>
                <span className="text-[11px] text-ink-soft dark:text-mist/50 shrink-0">{d.project} · {d.chunks ?? '?'} chunks{d.curated_excluded ? ' · excluded in Library' : ''}</span>
              </li>
            ))}
            {!docsQ.isLoading && !docs.length && <li className="text-ink-soft dark:text-mist/50">No documents.</li>}
          </ul>
        </>
      )}
    </Fold>
  )
}

const gapKey = (x) => `${String(x?.topic || '').trim().toLowerCase()}\u0000${String(x?.gap || '').trim().toLowerCase()}`

// The gaps are the brief's own audit: they change with a new brief, not with Re-check
// (which only re-grades coverage). A report added to the project after the brief is
// read only by the next brief, so the card says so.
function GapsCard({ gaps, runs = [], briefAt, onRebrief, busy }) {
  const researched = new Set(runs.filter((r) => r.status === 'done').flatMap((r) => (r.items ?? []).map(gapKey)))
  const added = runs.map((r) => r.meta?.indexed?.at).filter(Boolean).sort().at(-1)
  const stale = added && briefAt && added > briefAt
  return (
    <Fold id="gaps" body="space-y-2" head={<h3 className="text-sm uppercase tracking-[0.18em] text-gold-deep dark:text-gold inline-flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Gaps in the sources ({gaps.length})</h3>}>
      {stale && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-400/15 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
          <span className="flex-1 min-w-[12rem]">This list is from the brief of {new Date(briefAt).toLocaleString()}. A research report was added to the project on {new Date(added).toLocaleString()}; Re-check only re-grades coverage. Re-brief so the brief and this gap audit read it.</span>
          {onRebrief && <button className={btnGhost} disabled={busy} onClick={onRebrief}><RefreshCw className="w-3.5 h-3.5" /> Re-brief</button>}
        </div>
      )}
      <ul className="space-y-1.5">
        {gaps.map((g, i) => (
          <li key={i} className="flex items-start gap-2 text-xs">
            {researched.has(gapKey(g)) && <span className="mt-0.5 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider shrink-0 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" title="A deep research run covered this gap">researched</span>}
            <span className={`mt-0.5 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider shrink-0 ${g.severity === 'missing' ? 'bg-red-500/15 text-red-700 dark:text-red-400' : 'bg-amber-400/20 text-amber-700 dark:text-amber-400'}`}>{g.severity}</span>
            <span className="flex-1 min-w-0 text-ink dark:text-mist">{g.topic && <span className="text-ink-soft dark:text-mist/60">{g.topic} — </span>}{g.gap}</span>
            {g.search_query && <Link className="underline text-[11px] shrink-0" to={discoverHref(g.search_query)} title={`Discover: ${g.search_query}`}>Find sources →</Link>}
          </li>
        ))}
      </ul>
    </Fold>
  )
}

// Deep research: the web, not this project, on what coverage and the gap audit found
// missing. Two deliberate steps: a run costs money, and the report is AI output that
// enters the project only after someone has read it.
const RESEARCH_COST = { standard: '≈ 1–3 USD', max: '≈ 3–7 USD' }

function ResearchRun({ run, series, onChanged }) {
  const [open, setOpen] = useState(false)
  const report = useQuery({ queryKey: ['overviews', 'research-report', run.id], queryFn: () => api.researchReport(run.id), enabled: open, staleTime: Infinity })
  const add = useMutation({ mutationFn: (project) => api.addResearchToProject({ rid: run.id, project }), onSuccess: onChanged })
  const remove = useMutation({ mutationFn: () => api.deleteResearch(run.id), onSuccess: onChanged })
  const rebuild = useMutation({ mutationFn: () => api.rebuildResearch(run.id), onSuccess: () => { report.refetch(); onChanged() } })
  const m = run.meta ?? {}
  const projects = series?.projects ?? []
  const [project, setProject] = useState(projects[0])
  const going = run.status === 'running'
  return (
    <li className="space-y-2 border-t border-parchment-border dark:border-midnight-600 pt-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="flex-1 min-w-[12rem] text-ink dark:text-mist">
          {new Date(run.created_at).toLocaleString()} · {run.items.length} {run.items.length === 1 ? 'gap' : 'gaps'}
          {going && <span className="text-ink-soft dark:text-mist/60"> · researching, usually 5–20 min <Loader2 className="inline w-3 h-3 animate-spin" /></span>}
          {run.status === 'failed' && <span className="text-red-600"> · failed: {run.error}</span>}
          {run.status === 'done' && <span className="text-ink-soft dark:text-mist/60"> · {m.sources?.count ?? 0} sources · {m.usage?.searches ?? 0} searches · {Math.round((m.chars ?? 0) / 1000)}k chars</span>}
        </span>
        {run.status === 'done' && <button className={btnGhost} onClick={() => setOpen(!open)}><FileText className="w-3.5 h-3.5" /> {open ? 'Hide report' : 'Read report'}</button>}
        {run.status === 'done' && <a className={btnGhost} href={api.researchReportUrl(run.id)}><Download className="w-3.5 h-3.5" /> .md</a>}
        {run.status === 'done' && !m.rebuilt_at && m.titles == null && (
          <button className={btnGhost} disabled={rebuild.isPending} onClick={() => rebuild.mutate()} title="Read the run back from Gemini (free) and build the report again: the whole answer, links instead of [cite] numbers, page titles. A report in a project is re-indexed.">
            {rebuild.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Rebuild report
          </button>
        )}
        {run.status === 'done' && !m.indexed && projects.length > 1 && (
          <select className={input} value={project} onChange={(e) => setProject(e.target.value)} aria-label="Project">
            {projects.map((p) => <option key={p}>{p}</option>)}
          </select>
        )}
        {run.status === 'done' && !m.indexed && (
          <button className={btn} disabled={add.isPending} onClick={() => add.mutate(project)} title="Index the report as a source of the project. Read it first: it is AI output.">
            {add.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderOpen className="w-3.5 h-3.5" />} Add to project
          </button>
        )}
        {!going && (
          <button className={btnGhost} disabled={remove.isPending} aria-label="Delete this run" onClick={() => { if (confirm(m.indexed ? 'Delete this run? The report stays in the project as a document.' : 'Delete this run and its report?')) remove.mutate() }}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {m.sources?.domains?.length > 0 && (
        <p className="text-[11px] text-ink-soft dark:text-mist/50">Sources: {m.sources.domains.slice(0, 12).map((d) => `${d.domain} ${d.n}`).join(' · ')}{m.sources.domains.length > 12 ? ` · ${m.sources.domains.length - 12} more` : ''}</p>
      )}
      {m.indexed && <p className="text-[11px] text-ink-soft dark:text-mist/60"><Check className="inline w-3 h-3" /> In project {m.indexed.project}{m.indexed.chunks != null ? ` · ${m.indexed.chunks} chunks` : ''}. Re-brief to use it.</p>}
      {add.data?.note && <p className="text-xs text-amber-700 dark:text-amber-400">{add.data.note}</p>}
      {(add.error || remove.error || rebuild.error) && <p className="text-sm text-red-600">{(add.error || remove.error || rebuild.error).message}</p>}
      {open && report.isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
      {open && report.error && <p className="text-sm text-red-600">{report.error.message}</p>}
      {open && report.data && <div className="max-h-[36rem] overflow-y-auto rounded-lg border border-parchment-border dark:border-midnight-600 p-3 text-sm"><Markdown>{report.data}</Markdown></div>}
    </li>
  )
}

function ResearchCard({ ep, series, runs, candidates, onChanged }) {
  const caps = useCapabilities()
  const [skipped, setSkipped] = useState(() => new Set())
  const available = caps.data?.research?.available
  const level = caps.data?.research?.level === 'max' ? 'max' : 'standard'
  const chosen = candidates.filter((_, i) => !skipped.has(i))
  const going = runs.some((r) => r.status === 'running')
  const start = useMutation({ mutationFn: () => api.startResearch({ id: ep.id, items: chosen }), onSuccess: onChanged })
  if (!candidates.length && !runs.length) return null
  const toggle = (i) => setSkipped((prev) => { const next = new Set(prev); if (next.has(i)) next.delete(i); else next.add(i); return next })
  return (
    <Fold id="research" head={<div>
        <h3 className="text-sm uppercase tracking-[0.18em] text-gold-deep dark:text-gold inline-flex items-center gap-1.5"><Globe className="w-4 h-4" /> Deep research</h3>
        <p className="text-[11px] text-ink-soft dark:text-mist/50">Gemini searches the open web for what the sources lack and writes a report with citations. Nothing enters the project until you add the report.</p>
      </div>}>
      {candidates.length > 0 && (
        <ul className="space-y-1">
          {candidates.map((c, i) => (
            <li key={i}>
              <label className="flex items-start gap-2 text-xs text-ink dark:text-mist">
                <input type="checkbox" className="mt-0.5 shrink-0" checked={!skipped.has(i)} onChange={() => toggle(i)} />
                <span className="min-w-0">{c.topic}{c.gap ? <span className="text-ink-soft dark:text-mist/60"> — {c.gap}</span> : <span className="text-ink-soft dark:text-mist/60"> — {c.severity} coverage</span>}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {candidates.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <button className={btn} disabled={!available || going || start.isPending || !chosen.length}
            onClick={() => { if (confirm(`Research ${chosen.length} ${chosen.length === 1 ? 'gap' : 'gaps'} on the web? This costs ${RESEARCH_COST[level]} on the Gemini key and takes 5–20 minutes.`)) start.mutate() }}>
            {start.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />} Research {chosen.length} {chosen.length === 1 ? 'gap' : 'gaps'}
          </button>
          <span className="text-[11px] text-ink-soft dark:text-mist/50">{RESEARCH_COST[level]} · 5–20 min{caps.data && !available ? ' · needs GEMINI_API_KEY in bff/.env' : ''}</span>
        </div>
      )}
      {start.error && <p className="text-sm text-red-600">{start.error.message}</p>}
      {runs.length > 0 && <ul className="space-y-2">{runs.map((r) => <ResearchRun key={r.id} run={r} series={series} onChanged={onChanged} />)}</ul>}
    </Fold>
  )
}

// Episode detail: brief · prompt pack · source pack
// ---------------------------------------------------------------------------

function EpisodeDetail({ id, onBack, onOpenEpisode }) {
  const qc = useQueryClient()
  const [tab, setTab] = useState('brief')
  const q = useQuery({
    queryKey: ['overviews', 'episode', id],
    queryFn: () => api.getEpisode(id),
    refetchInterval: (query) => (query.state.data?.episode?.busy ? 3000 : query.state.data?.research?.some((r) => r.status === 'running') ? 15000 : false),
  })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['overviews'] })
  const brief = useMutation({ mutationFn: () => api.briefEpisode(id), onSuccess: invalidate })
  const prompts = useMutation({ mutationFn: () => api.promptEpisode(id), onSuccess: invalidate })
  const ep = q.data?.episode
  const series = q.data?.series
  useEffect(() => {
    if (!ep) return
    if (tab === 'brief' && !ep.brief_md && ep.prompt_pack) setTab('prompts')
  }, [ep?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  if (!ep) return q.isError ? <p className="text-sm text-red-600">{q.error?.message}</p> : <Loader2 className="w-5 h-5 animate-spin" />

  const r = rank(ep.status)
  const assets = q.data?.assets ?? []
  const native = series?.generator === 'native'
  const deep = ep.kind === 'deepdive'
  const parent = q.data?.parent ?? null
  const children = q.data?.children ?? []
  const scope = q.data?.scope_resolved ?? null
  // deep dives created after the prompt pack: the narration cannot hand over to them
  const familyChanged = ep.prompt_pack?.generated_at
    ? children.filter((c) => new Date(c.created_at) > new Date(ep.prompt_pack.generated_at)).length
    : 0
  const tabs = [
    ['brief', 'Brief', FileText],
    ['prompts', 'Prompt pack', Wand2],
    ...(!deep ? [['deepdives', `Deep dives${children.length ? ` (${children.length})` : ''}`, Layers]] : []),
    // F2: the native generator is an alternative on any series (the brief is its input)
    ...(native || ep.script || ep.brief_md ? [['script', `${native ? 'Script' : 'Native alternative'}${ep.script?.turns ? ` (${ep.script.turns})` : ''}`, ScrollText]] : []),
    ['sources', `Source pack${ep.source_pack?.length ? ` (${ep.source_pack.length})` : ''}`, Download],
    ['asset', `Asset${assets.length ? ` (${assets.length})` : ''}`, series?.format === 'video' ? Video : Headphones],
    ['publish', `Publish${(q.data?.publications ?? []).length ? ` (${q.data.publications.length})` : ''}`, ExternalLink],
  ]

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-3">
        <button className={btnGhost} onClick={onBack}><ChevronLeft className="w-4 h-4" /> Episodes</button>
        <div className="basis-full sm:basis-0 flex-1 min-w-0">
          <h2 className="font-display text-xl sm:text-2xl text-ink dark:text-mist sm:truncate">#{ep.label ?? ep.ordinal} {ep.title}</h2>
          <p className="text-xs text-ink-soft dark:text-mist/50 line-clamp-2 sm:line-clamp-none">
            {series?.title} · {deep && parent
              ? <>deep dive from <button className="underline" onClick={() => onOpenEpisode?.(parent.id)}>#{parent.label} {parent.title}</button></>
              : ep.topics.map((t) => t.title).join(' · ')}
          </p>
        </div>
        <StatusChip status={ep.status} />
        <button className={btnGhost} disabled={ep.busy || brief.isPending} onClick={async () => { if (await confirmBrief(ep)) brief.mutate() }}>
          <RefreshCw className="w-3.5 h-3.5" /> {ep.brief_md ? 'Re-brief' : 'Brief'}
        </button>
        <button className={native ? btnGhost : btn} disabled={ep.busy || prompts.isPending || !ep.brief_md} onClick={() => prompts.mutate()}>
          <Wand2 className="w-3.5 h-3.5" /> {ep.prompt_pack?.youtube?.title ? 'Re-prompt' : 'Prompts'}
        </button>
        {native && (
          <button className={btn} disabled={ep.busy || !ep.brief_md} onClick={() => setTab('script')} title="Write the two-host script, then generate the episode without NotebookLM">
            <ScrollText className="w-3.5 h-3.5" /> Script
          </button>
        )}
      </div>
      {(brief.isError || prompts.isError) && <p className="text-sm text-red-600">{brief.error?.message || prompts.error?.message}</p>}
      {ep.status === 'failed' && ep.last_error && (
        <p className="text-sm text-red-600 inline-flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {ep.last_error}</p>
      )}
      {ep.busy && (
        <p className="text-sm text-ink-soft dark:text-mist/60 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          {BUSY_TEXT(ep, series)}
        </p>
      )}

      <CostCard costs={q.data?.costs} />

      {deep && <DeepDiveFocus ep={ep} series={series} parent={parent} onChanged={invalidate} onOpen={onOpenEpisode} />}

      <div className="inline-flex flex-wrap max-w-full rounded-lg border border-parchment-border dark:border-midnight-600 p-0.5">
        {tabs.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs transition ${tab === k ? 'bg-gold/15 text-gold-deep dark:text-gold' : 'text-ink-soft dark:text-mist/60'}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {tab === 'brief' && scope && series && (
        <ScopeCard
          seriesId={series.id}
          scope={scope}
          note={scope.source === 'episode' ? 'own scope for this episode' : `inherited from the ${scope.source}`}
          onSave={(d) => api.patchEpisode({ id: ep.id, scope: d }).then(invalidate)}
          onReset={scope.source === 'episode' ? () => api.patchEpisode({ id: ep.id, scope: null }).then(invalidate) : null}
        />
      )}
      {tab === 'brief' && <CoverageCard ep={ep} scope={scope} onChanged={invalidate} />}
      {tab === 'brief' && ep.brief_meta?.gaps?.length > 0 && <GapsCard gaps={ep.brief_meta.gaps} runs={q.data?.research ?? []} briefAt={ep.brief_meta.generated_at} busy={ep.busy || brief.isPending} onRebrief={async () => { if (await confirmBrief(ep)) brief.mutate() }} />}
      {tab === 'brief' && <ResearchCard ep={ep} series={series} runs={q.data?.research ?? []} candidates={q.data?.research_candidates ?? []} onChanged={invalidate} />}
      {tab === 'brief' && (
        ep.brief_md ? (
          <Fold id="brief" defaultOpen head={
            <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-ink-soft dark:text-mist/50">
              <span className="min-w-0">
                <span className="mr-2 text-sm uppercase tracking-[0.18em] text-gold-deep dark:text-gold inline-flex items-center gap-1.5"><FileText className="w-4 h-4" /> Brief</span>
                {ep.brief_meta?.cited_documents ?? 0} documents cited ·{' '}
                {(ep.brief_meta?.asks ?? []).map((a) => `${a.topic}: ${a.confidence?.tier ?? a.confidence ?? '?'}`).join(' · ')}
              </span>
              <a className={btnGhost} href={api.briefFileUrl(ep.id)}><Download className="w-3.5 h-3.5" /> brief.md</a>
            </div>}>
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <Markdown>{ep.brief_md}</Markdown>
            </div>
          </Fold>
        ) : (
          <p className="text-sm text-ink-soft dark:text-mist/60">
            {ep.kind === 'deepdive'
              ? 'No brief yet. "Brief" researches each sub-topic in depth (its question, two follow-up questions, merged with citations)' + (ep.focus?.truth_lens ? ' and weighs key claims with the Truth lens.' : '.')
              : 'No brief yet. "Brief" asks the corpus about each topic and keeps the citations.'}
          </p>
        )
      )}

      {tab === 'prompts' && familyChanged > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-300">The prompt pack predates {familyChanged} deep dive{familyChanged > 1 ? 's' : ''} of this episode — Re-prompt so the narration hands over to them (the YouTube text is regenerated too; manual edits to it are lost).</p>
      )}
      {tab === 'prompts' && (ep.prompt_pack?.youtube?.title ? <PromptPack ep={ep} series={series} /> : (
        <p className="text-sm text-ink-soft dark:text-mist/60">No prompt pack yet — brief the episode first, then "Prompts".</p>
      ))}

      {tab === 'deepdives' && <DeepDivesCard ep={ep} series={series} children={children} onChanged={invalidate} onOpen={onOpenEpisode} />}

      {tab === 'script' && <ScriptPanel ep={ep} series={series} assets={assets} audio={q.data?.audio_status} onChanged={invalidate} onGenerated={() => setTab('asset')} />}

      {tab === 'sources' && <SourcePack ep={ep} />}

      {tab === 'asset' && <AssetPanel ep={ep} series={series} assets={assets} onChanged={invalidate} />}

      {tab === 'publish' && <PublishPanel ep={ep} series={series} assets={assets} publications={q.data?.publications ?? []} onChanged={invalidate} />}

      {native && r >= ORDER.indexOf('prompted') && r < ORDER.indexOf('generating') && !ep.busy && (
        <div className={`${card} p-4 text-sm text-ink-soft dark:text-mist/60 space-y-1`}>
          <div className="text-ink dark:text-mist">Next: generate natively</div>
          <ol className="list-decimal pl-5 space-y-0.5">
            <li>{ep.script ? 'Read the script, fix what you want in the text, Save.' : 'Script → "Write script" turns the brief into a two-host dialogue (one slide and one chapter per segment).'}</li>
            <li>"Generate {series.format}" voices every turn with the series' voices, mixes the track and renders the {series.format === 'video' ? 'slides video' : 'cover and audiogram'} — captions and chapters come from the script, no transcription needed.</li>
          </ol>
        </div>
      )}

      {r >= ORDER.indexOf('awaiting_asset') && r < ORDER.indexOf('processing') && series?.generator === 'handoff' && (
        <div className={`${card} p-4 text-sm text-ink-soft dark:text-mist/60 space-y-1`}>
          <div className="text-ink dark:text-mist">Next: generate in NotebookLM</div>
          <ol className="list-decimal pl-5 space-y-0.5">
            <li>New notebook → add the sources from the Source pack (download the files; paste the YouTube URLs).</li>
            <li>Add <code>brief.md</code> as a source too — it keeps the overview on this episode's rails.</li>
            <li>Studio → {series.format === 'video' ? 'Video' : 'Audio'} Overview → Customize → paste the prompt → Generate.</li>
            <li>Download the result and drop it into the <button className="underline" onClick={() => setTab('asset')}>Asset</button> tab — Veralis transcribes it, places chapters, renders the cover and (for audio) the audiogram.</li>
          </ol>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Deep dives (doc/plan-deepdive-packs.md): plan children under a briefed
// overview episode; a deep dive's own focus (length, truth lens, re-outline).
// ---------------------------------------------------------------------------

const LENGTH_HINT = { short: '≈ 8–12 min', default: '≈ 12–20 min', long: '≈ 20–35 min' }
// a NotebookLM Video Overview renders ≈ 9 min whatever it gets: for video the
// length only drives the sub-topic budget of the outline
const VIDEO_LENGTH_HINT = { short: 'NotebookLM ≈ 9 min · 2–3 sub-topics', default: 'NotebookLM ≈ 9 min · 3–4 sub-topics', long: '"Longer" if NotebookLM offers it · 4–5 sub-topics' }
const lengthHint = (k, format) => (format === 'video' ? VIDEO_LENGTH_HINT[k] : LENGTH_HINT[k])
const miniSelect = 'h-7 rounded-md px-2 text-[11px] bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist'

function DeepDivesCard({ ep, series, children, onChanged, onOpen }) {
  const caps = useCapabilities()
  const d = caps.data?.defaults ?? {}
  const [length, setLength] = useState('')
  const [truthLens, setTruthLens] = useState(false)
  const [angle, setAngle] = useState('')
  const plan = useMutation({ mutationFn: (body) => api.planDeepdives({ id: ep.id, ...body }), onSuccess: onChanged })
  const taken = new Map(children.filter((c) => c.topic_index != null).map((c) => [c.topic_index, c]))
  const eff = length || d.deepdive_length || 'default'
  const body = { length: eff, truth_lens: truthLens, angle: angle.trim() || undefined }
  const remaining = ep.topics.filter((_, i) => !taken.has(i)).length
  const ready = !!ep.brief_md && !ep.busy && !plan.isPending
  return (
    <div className="space-y-3">
      <Fold id="deepdive-plan" head={<div className="text-sm text-ink dark:text-mist inline-flex items-center gap-1.5"><Layers className="w-4 h-4" /> Plan deep dives</div>}>
        {!ep.brief_md && <p className="text-sm text-ink-soft dark:text-mist/60">Brief this episode first — a deep dive's outline is grounded in the brief.</p>}
        {series?.format === 'video' && (
          <p className="text-xs text-amber-700 dark:text-amber-300">NotebookLM renders a video of ≈ 9 minutes whatever it is given — 3–4 deep sub-topics beat seven shallow ones (the outline follows the length chosen here).</p>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <label className="block space-y-1">
            <span className="text-[11px] text-ink-soft dark:text-mist/50">Length</span>
            <select className={input} value={eff} onChange={(e) => setLength(e.target.value)}>
              {['short', 'default', 'long'].map((k) => <option key={k} value={k}>{k} · {lengthHint(k, series?.format)}</option>)}
            </select>
          </label>
          <label className="block space-y-1 flex-1 min-w-[16rem]">
            <span className="text-[11px] text-ink-soft dark:text-mist/50">Angle (optional)</span>
            <input className={`${input} w-full`} value={angle} onChange={(e) => setAngle(e.target.value)} placeholder="e.g. practical consequences for small builders" />
          </label>
          <label className="inline-flex items-center gap-2 h-10 text-sm text-ink dark:text-mist">
            <input type="checkbox" checked={truthLens} onChange={(e) => setTruthLens(e.target.checked)} /> Truth lens on the deep brief
          </label>
          <button className={btn} disabled={!ready || remaining === 0} onClick={() => plan.mutate({ ...body, topic_index: 'all' })}>
            {plan.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} All remaining topics ({remaining})
          </button>
        </div>
        <ul className="divide-y divide-parchment-border dark:divide-midnight-600">
          {ep.topics.map((t, i) => {
            const c = taken.get(i)
            return (
              <li key={i} className="py-2 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-ink dark:text-mist truncate">{t.title}</div>
                  {t.description && <div className="text-[11px] text-ink-soft dark:text-mist/50 truncate">{t.description}</div>}
                </div>
                {c ? (
                  <button className={btnGhost} onClick={() => onOpen?.(c.id)} title={c.title}>#{c.label} <StatusChip status={c.status} /></button>
                ) : (
                  <button className={btnGhost} disabled={!ready} onClick={() => plan.mutate({ ...body, topic_index: i })}><Sparkles className="w-3.5 h-3.5" /> Plan deep dive</button>
                )}
              </li>
            )
          })}
        </ul>
        <p className="text-[11px] text-ink-soft dark:text-mist/50">
          Planning is one LLM call per deep dive (~20–30 s): a title, a summary and 4–8 sub-topics with research questions. The deep brief afterwards costs about 3 asks per sub-topic plus merges — the estimate is shown before it starts.
        </p>
        {plan.isError && <p className="text-sm text-red-600">{plan.error?.message}</p>}
      </Fold>
      {children.length > 0 && (
        <Fold id="deepdive-children" defaultOpen body="space-y-1" head={<div className="text-[11px] uppercase tracking-[0.18em] text-gold-deep dark:text-gold">Deep dives of this episode ({children.length})</div>}>
          {children.map((c) => (
            <button key={c.id} className="w-full flex items-center gap-3 text-left py-1" onClick={() => onOpen?.(c.id)}>
              <span className="text-[11px] text-ink-soft dark:text-mist/50 w-8">#{c.label}</span>
              <span className="flex-1 text-sm text-ink dark:text-mist truncate">{c.title}</span>
              <StatusChip status={c.status} />
            </button>
          ))}
        </Fold>
      )}
    </div>
  )
}

function DeepDiveFocus({ ep, series, parent, onChanged, onOpen }) {
  const f = ep.focus ?? {}
  const patch = useMutation({ mutationFn: (focus) => api.patchEpisode({ id: ep.id, focus }), onSuccess: onChanged })
  const outline = useMutation({ mutationFn: () => api.planDeepdives({ id: ep.id }), onSuccess: onChanged })
  return (
    <div className={`${card} p-4 space-y-2`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-gold/15 text-gold-deep dark:text-gold">Deep dive</span>
        <span className="text-sm text-ink dark:text-mist">on “{f.topic?.title || ep.title}”</span>
        {parent && <button className="text-sm underline text-ink-soft dark:text-mist/70" onClick={() => onOpen?.(parent.id)}>from #{parent.label} {parent.title}</button>}
        <span className="flex-1" />
        <label className="inline-flex items-center gap-1 text-[11px] text-ink-soft dark:text-mist/50">Length
          <select className={miniSelect} value={f.length || 'default'} disabled={ep.busy || patch.isPending} onChange={(e) => patch.mutate({ length: e.target.value })}>
            {['short', 'default', 'long'].map((k) => <option key={k} value={k}>{k} · {lengthHint(k, series?.format)}</option>)}
          </select>
        </label>
        <label className="inline-flex items-center gap-1 text-[11px] text-ink-soft dark:text-mist/50">
          <input type="checkbox" checked={!!f.truth_lens} disabled={ep.busy || patch.isPending} onChange={(e) => patch.mutate({ truth_lens: e.target.checked })} /> Truth lens
        </label>
        <button className={btnGhost} disabled={ep.busy || outline.isPending} title="Propose the sub-topics again (one LLM call)"
          onClick={() => { if (!ep.brief_md || confirm('Re-outline replaces the sub-topics; the existing brief stays until you re-brief. Continue?')) outline.mutate() }}>
          {outline.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Re-outline
        </button>
      </div>
      {f.angle && <p className="text-[11px] text-ink-soft dark:text-mist/50">Angle: {f.angle}</p>}
      {(f.subtopics ?? []).length > 0 && (
        <ol className="list-decimal pl-5 space-y-0.5 text-sm text-ink dark:text-mist">
          {f.subtopics.map((s, i) => <li key={i}>{s.title}{s.question && <span className="text-ink-soft dark:text-mist/50"> — {s.question}</span>}</li>)}
        </ol>
      )}
      <p className="text-[11px] text-ink-soft dark:text-mist/50">Length scales the NotebookLM prompt and the native script (×1.5 / ×2 / ×3 of the series target); the YouTube description opens with the link to the parent and the parent lists its deep dives.</p>
      {(patch.isError || outline.isError) && <p className="text-sm text-red-600">{patch.error?.message || outline.error?.message}</p>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Asset (O2): drop zone + what was derived from the drop
// ---------------------------------------------------------------------------

// API list prices (Settings → Overviews); an ElevenLabs plan prepays characters
const fmtUsd = (v) => (!v ? '$0' : v < 0.01 ? '< $0.01' : `$${v.toFixed(2)}`)
const COST_STEP = {
  plan: 'Series plan', brief: 'Brief', 'deepdive-plan': 'Deep dive plan', prompts: 'Prompts', script: 'Script', prosody: 'Delivery marks',
  layouts: 'Slide layouts', generate: 'Voices & mix', compose: 'Intro / outro', thumbnail: 'AI thumbnail', research: 'Deep research', other: 'Other',
}
const COST_KIND = { llm: 'LLM', tts: 'voices', image: 'images', music: 'music', research: 'research' }
const kindLine = (kinds) => Object.entries(kinds ?? {}).filter(([, v]) => v > 0).map(([k, v]) => `${COST_KIND[k] || k} ${fmtUsd(v)}`).join(' · ')

function CostCard({ costs }) {
  if (!costs?.lines) return null
  return (
    <details className={`${card} px-4 py-2 text-xs text-ink-soft dark:text-mist/70`}>
      <summary className="cursor-pointer text-sm text-ink dark:text-mist">
        Spent ≈ {fmtUsd(costs.total)} <span className="text-[11px] text-ink-soft dark:text-mist/50">· {kindLine(costs.kinds)}</span>
      </summary>
      <table className="mt-2 w-full">
        <tbody>
          {costs.steps.map((st) => (
            <tr key={st.step} className="border-t border-parchment-border dark:border-midnight-600">
              <td className="py-1 pr-2 text-ink dark:text-mist">{COST_STEP[st.step] || st.step}{st.estimated ? ' *' : ''}</td>
              <td className="py-1 pr-2">{kindLine(st.kinds)}</td>
              <td className="py-1 text-right tabular-nums text-ink dark:text-mist">{fmtUsd(st.usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-[11px]">
        {costs.units?.tts_chars ? `${costs.units.tts_chars.toLocaleString()} characters voiced · ` : ''}API list prices from Settings → Overviews; an ElevenLabs plan prepays its characters.
        {costs.estimated ? ' * estimated: work done before costs were booked, or a price Google does not publish per call.' : ''}
      </p>
    </details>
  )
}

const fmtDur = (s) => {
  if (!s) return ''
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${String(sec).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// Script (O4): the two-host dialogue, editable as screenplay text, and the
// native generation that voices it.
// ---------------------------------------------------------------------------

const VOICE_HINT = {
  google: 'Google Cloud TTS voice names, e.g. cs-CZ-Chirp3-HD-Charon / cs-CZ-Chirp3-HD-Aoede (defaults per language when blank)',
  elevenlabs: 'ElevenLabs voice IDs from your account (blank = the engine default); model e.g. eleven_multilingual_v2',
}

const PROSODY_OK = (v) => (v?.provider || 'google') === 'elevenlabs' && /v3|v4/i.test(String(v?.model_id || ''))

function VoiceSettings({ series }) {
  const qc = useQueryClient()
  const v = series.voice ?? {}
  const m = v.music ?? {}
  const fromSeries = () => ({
    provider: v.provider || 'google', voice_a: v.voice_a || '', voice_b: v.voice_b || '', model_id: v.model_id || '',
    gender_a: v.gender_a || 'male', gender_b: v.gender_b || 'female', prosody: v.prosody || 'light',
    music_source: m.source ?? '', music_prompt: m.prompt || '', music_file: m.file || '',
    music_volume_pct: m.volume_pct ?? '', music_duck_pct: m.duck_pct ?? '', music_fade_in_s: m.fade_in_s ?? '', music_fade_out_s: m.fade_out_s ?? '',
  })
  const [form, setForm] = useState(fromSeries)
  useEffect(() => setForm(fromSeries()), [series.id, series.updated_at]) // eslint-disable-line react-hooks/exhaustive-deps
  const toVoice = (f) => ({
    provider: f.provider, voice_a: f.voice_a || null, voice_b: f.voice_b || null, model_id: f.model_id || null,
    gender_a: f.gender_a, gender_b: f.gender_b, prosody: f.prosody,
    music: f.music_source === '' ? null : {
      source: f.music_source, prompt: f.music_prompt || null, file: f.music_file || null,
      volume_pct: f.music_volume_pct === '' ? null : Number(f.music_volume_pct), duck_pct: f.music_duck_pct === '' ? null : Number(f.music_duck_pct),
      fade_in_s: f.music_fade_in_s === '' ? null : Number(f.music_fade_in_s), fade_out_s: f.music_fade_out_s === '' ? null : Number(f.music_fade_out_s),
    },
  })
  const save = useMutation({
    mutationFn: () => api.patchSeries({ id: series.id, voice: toVoice(form) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['overviews'] }),
  })
  const dirty = JSON.stringify(form) !== JSON.stringify(fromSeries())
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value })
  const prosodyOk = PROSODY_OK({ provider: form.provider, model_id: form.model_id })
  return (
    <Fold id="voices" body="space-y-2" head={
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-ink dark:text-mist inline-flex items-center gap-1.5"><Mic className="w-4 h-4" /> Voices &amp; sound (whole series)</div>
        <button className={btn} disabled={!dirty || save.isPending} onClick={() => save.mutate()}>{save.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save</button>
      </div>}>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="block space-y-1">
          <span className="text-[11px] text-ink-soft dark:text-mist/50">Provider</span>
          <select className={`${input} w-full`} value={form.provider} onChange={set('provider')}>
            <option value="google">Google Cloud TTS</option>
            <option value="elevenlabs">ElevenLabs</option>
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-[11px] text-ink-soft dark:text-mist/50">A · host</span>
          <div className="flex gap-1">
            <input className={`${input} w-full min-w-0`} value={form.voice_a} onChange={set('voice_a')} placeholder="default" />
            <select className={`${input} w-20 shrink-0`} value={form.gender_a} onChange={set('gender_a')} title="Who speaks as A — the script writer needs it (průvodce / průvodkyně)">
              <option value="male">man</option>
              <option value="female">woman</option>
            </select>
          </div>
        </label>
        <label className="block space-y-1">
          <span className="text-[11px] text-ink-soft dark:text-mist/50">B · expert</span>
          <div className="flex gap-1">
            <input className={`${input} w-full min-w-0`} value={form.voice_b} onChange={set('voice_b')} placeholder="default" />
            <select className={`${input} w-20 shrink-0`} value={form.gender_b} onChange={set('gender_b')} title="Who speaks as B">
              <option value="male">man</option>
              <option value="female">woman</option>
            </select>
          </div>
        </label>
        <label className="block space-y-1">
          <span className="text-[11px] text-ink-soft dark:text-mist/50">Model (ElevenLabs)</span>
          <input className={`${input} w-full`} value={form.model_id} disabled={form.provider !== 'elevenlabs'} onChange={set('model_id')} placeholder="eleven_v4" />
        </label>
      </div>
      <p className="text-[11px] text-ink-soft dark:text-mist/50">
        Language for the voices: <b>{series.language}</b> (the series language, from Settings when the series was made){form.provider === 'elevenlabs' ? ` — ElevenLabs gets language_code "${String(series.language || 'cs').slice(0, 2)}", which fixes pronunciation and how numbers and dates are read` : ''}.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="block space-y-1">
          <span className="text-[11px] text-ink-soft dark:text-mist/50">Delivery marks {prosodyOk ? '' : '(needs ElevenLabs v3/v4)'}</span>
          <select className={`${input} w-full`} value={form.prosody} disabled={!prosodyOk} onChange={set('prosody')}>
            <option value="off">Off</option>
            <option value="light">Light — breaths, pauses, “hmm”</option>
            <option value="rich">Rich — plus laughs, throat clear</option>
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-[11px] text-ink-soft dark:text-mist/50">Music bed</span>
          <select className={`${input} w-full`} value={form.music_source} onChange={set('music_source')}>
            <option value="">Follow Settings</option>
            <option value="off">Off</option>
            <option value="elevenlabs">Eleven Music (generated)</option>
            <option value="file">Own file</option>
          </select>
        </label>
        {form.music_source === 'file' && (
          <label className="block space-y-1 md:col-span-2">
            <span className="text-[11px] text-ink-soft dark:text-mist/50">Music file (absolute path)</span>
            <input className={`${input} w-full`} value={form.music_file} onChange={set('music_file')} placeholder="C:/music/bed.mp3" />
          </label>
        )}
        {form.music_source === 'elevenlabs' && (
          <label className="block space-y-1 md:col-span-2">
            <span className="text-[11px] text-ink-soft dark:text-mist/50">Music prompt (English; blank = Settings / calm felt piano, no drums)</span>
            <input className={`${input} w-full`} value={form.music_prompt} onChange={set('music_prompt')} placeholder="Calm ambient instrumental, soft piano and pads, no drums, no vocals, loopable" />
          </label>
        )}
        {(form.music_source === 'elevenlabs' || form.music_source === 'file') && (<>
          <label className="block space-y-1">
            <span className="text-[11px] text-ink-soft dark:text-mist/50">Volume % in pauses / % kept under speech</span>
            <div className="flex gap-1">
              <input className={`${input} w-full`} type="number" step="1" min="1" max="100" value={form.music_volume_pct} onChange={set('music_volume_pct')} placeholder="12" />
              <input className={`${input} w-full`} type="number" step="1" min="1" max="100" value={form.music_duck_pct} onChange={set('music_duck_pct')} placeholder="25" />
            </div>
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] text-ink-soft dark:text-mist/50">Fade in / fade out (s)</span>
            <div className="flex gap-1">
              <input className={`${input} w-full`} type="number" step="0.5" min="0" max="30" value={form.music_fade_in_s} onChange={set('music_fade_in_s')} placeholder="2" />
              <input className={`${input} w-full`} type="number" step="0.5" min="0" max="30" value={form.music_fade_out_s} onChange={set('music_fade_out_s')} placeholder="4" />
            </div>
          </label>
        </>)}
      </div>
      <p className="text-[11px] text-ink-soft dark:text-mist/50">{VOICE_HINT[form.provider]} The man/woman choice tells the script writer which grammatical forms each host uses about themselves. Delivery marks are inline tags like [inhalation] or [pause] in the script — performed by ElevenLabs v3/v4, stripped from captions.</p>
      {save.isError && <p className="text-sm text-red-600">{save.error?.message}</p>}
    </Fold>
  )
}

// Which slide the caret is on: a heading line starts the next one.
const segmentAt = (text, caret) => Math.max(0, (text.slice(0, caret).match(/^#+\s*\S/gm)?.length ?? 1) - 1)

// F4.4: the slide under the caret, from the text as it stands: an edit shows here
// before it is saved, and a broken line says what is wrong while the last good
// picture stays.
function ScriptSlides({ ep, text, segment, setSegment }) {
  const [draft, setDraft] = useState(text)
  useEffect(() => {
    const wait = setTimeout(() => setDraft(text), 600)
    return () => clearTimeout(wait)
  }, [text])
  const deck = useQuery({
    queryKey: ['overviews', 'slide-preview', ep.id, draft],
    queryFn: () => api.previewScript({ id: ep.id, text: draft }),
    placeholderData: (previous) => previous,
    retry: false,
  })
  // a failed query has no data, placeholder or not
  const good = useRef(null)
  if (deck.data) good.current = deck.data
  const shown = good.current
  const slides = shown?.slides ?? []
  const index = Math.min(segment, Math.max(0, slides.length - 1))
  const slide = slides[index]
  return (
    <div className="space-y-2 lg:order-2 lg:sticky lg:top-4 min-w-0">
      {slides.length > 0 && (
        <select className={`${input} w-full`} value={index} onChange={(e) => setSegment(Number(e.target.value))} aria-label="Slide">
          {slides.map((x, i) => <option key={i} value={i}>{i + 1}. {x.data.title} · {x.template}</option>)}
        </select>
      )}
      {slide && <SlidePreview slide={slide} />}
      {deck.isLoading && <Loader2 className="w-4 h-4 animate-spin text-gold-deep dark:text-gold" />}
      {deck.error && <p className="text-sm text-red-600">{deck.error.message}</p>}
      {shown && (
        <p className="text-[11px] text-ink-soft dark:text-mist/50">
          {shown.timing === 'audio' ? 'Timing of the voiced audio.' : 'Estimated timing: this dialogue has no audio yet.'}
          {shown.renderer === 'ffmpeg' ? ' The renderer in Settings is ffmpeg stills, so the video will not look like this.' : ''}
        </p>
      )}
    </div>
  )
}

// What a regenerate would do, in one sentence: how many turns get new voices, what it
// costs at the API price, and which change asks for it.
function regenerateText(a, series) {
  const what = a.state === 'remix'
    ? 'Re-mix only — every turn keeps its voice, no TTS cost'
    : a.to_voice === a.turns
      ? `Re-voices all ${a.turns} turns (${a.chars.toLocaleString()} characters ≈ ${fmtUsd(a.usd)})`
      : `Re-voices ${a.to_voice} of ${a.turns} turns (${a.chars.toLocaleString()} characters ≈ ${fmtUsd(a.usd)}), the other ${a.turns - a.to_voice} keep their voices`
  const render = series?.format === 'video' ? ', then renders the slides video again' : ', then the audiogram again'
  return `${what}${render}.${a.reasons?.length ? ` Why: ${a.reasons.join('; ')}.` : ''}`
}

function ScriptPanel({ ep, series, assets, audio, onChanged, onGenerated }) {
  const [text, setText] = useState(ep.script_text ?? '')
  useEffect(() => setText(ep.script_text ?? ''), [ep.script?.generated_at, ep.script?.edited_at]) // eslint-disable-line react-hooks/exhaustive-deps
  const [previewing, setPreviewing] = useState(false)
  const [segment, setSegment] = useState(0)
  const write = useMutation({ mutationFn: () => api.writeScript(ep.id), onSuccess: onChanged })
  const save = useMutation({ mutationFn: () => api.saveScript({ id: ep.id, text }), onSuccess: onChanged })
  const generate = useMutation({ mutationFn: (opts) => api.generateEpisode(ep.id, opts), onSuccess: () => { onChanged(); onGenerated?.() } })
  // F6.2: delivery marks (ElevenLabs v3/v4 only) — LLM pass or strip
  const prosody = useMutation({ mutationFn: (remove) => api.prosodyScript({ id: ep.id, remove }), onSuccess: onChanged })
  const prosodyOk = PROSODY_OK(series?.voice)
  // publication pass: gender of address, no talk of sources or gaps
  const polish = useMutation({ mutationFn: () => api.polishScript(ep.id), onSuccess: onChanged })
  // F4.3: slide layouts (stats / quote / compare) on a finished script
  const layouts = useMutation({ mutationFn: (remove) => api.layoutsScript({ id: ep.id, remove }), onSuccess: onChanged })
  const dirty = text !== (ep.script_text ?? '')
  const s = ep.script
  const hasNative = assets.some((a) => a.origin === 'native')
  // slides only, over the audio that exists: no TTS credits
  const rerender = useMutation({ mutationFn: () => api.rerenderSlides(ep.id), onSuccess: () => { onChanged(); onGenerated?.() } })
  const canRerender = series?.format === 'video' && assets.some((a) => a.origin === 'native' && a.kind === 'audio' && a.meta?.timeline)
  const err = write.error || save.error || generate.error || prosody.error || layouts.error || rerender.error || polish.error
  const layoutCount = (s?.segments ?? []).filter((x) => x.layout).length
  return (
    <div className="space-y-3">
      {series?.generator !== 'native' && (
        <Fold id="native-note" className="text-xs text-ink-soft dark:text-mist/70" body="space-y-1" head={<div className="text-sm text-ink dark:text-mist">Native alternative to the NotebookLM file</div>}>
          <p>Writes a two-voice script straight from the brief (one LLM call, length follows the episode: deep dives ×1.5 / ×2 / ×3 words), voices it with the configured TTS and renders {series?.format === 'video' ? 'a slides video with the intro card' : 'an audiogram'}. The result is added next to the dropped file — nothing is replaced — and each media asset shows how much of the brief it actually carries, so you can compare and pick the one to publish in the Publish tab. NotebookLM caps a video at ≈ 9 minutes; this path has no such cap.</p>
        </Fold>
      )}
      {series && <VoiceSettings series={series} />}
      <Fold id="script" defaultOpen head={
          <div className="text-sm text-ink dark:text-mist">
            {s ? (
              <>
                Script · {s.segments?.length} segments · {s.turns} turns · {s.words} words · ≈ {fmtDur(s.est_seconds)}
                {s.target_words ? <span className="text-ink-soft dark:text-mist/50"> (target ≈ {s.target_words})</span> : null}
                {s.edited_at ? <span className="text-ink-soft dark:text-mist/50"> · edited</span> : null}
              </>
            ) : 'No script yet.'}
          </div>}>
        <div className="flex flex-wrap items-center gap-2">
          {s && <a className={btnGhost} href={api.scriptFileUrl(ep.id)}><Download className="w-3.5 h-3.5" /> script.txt</a>}
          <button className={s ? btnGhost : btn} disabled={ep.busy || write.isPending || !ep.brief_md} onClick={() => { if (!s || confirm('Rewrite the script from the brief? Your edits are replaced.')) write.mutate() }} title={ep.brief_md ? 'Turn the brief into a two-host dialogue' : 'Brief the episode first'}>
            {write.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {s ? 'Rewrite' : 'Write script'}
          </button>
          {s && (
            <button className={dirty ? btn : btnGhost} disabled={!dirty || ep.busy || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
            </button>
          )}
          {s && prosodyOk && (
            <button className={btnGhost} disabled={ep.busy || dirty || prosody.isPending} onClick={() => prosody.mutate(false)}
              title={dirty ? 'Save the script first' : `Insert ${series?.voice?.prosody === 'rich' ? 'rich' : 'light'} delivery marks — breaths, pauses, “hmm” — into this script (one LLM pass; the words stay)`}>
              {prosody.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} {s.tags ? `Delivery marks (${s.tags})` : 'Delivery marks'}
            </button>
          )}
          {s && (
            <button className={btnGhost} disabled={ep.busy || dirty || polish.isPending} onClick={() => polish.mutate()}
              title={dirty ? 'Save the script first' : 'One LLM pass before publishing: the hosts address each other in the right grammatical gender, and nothing is said about sources, materials, the audit or gaps. Other words stay; Regenerate audio then re-voices only the changed turns.'}>
              {polish.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} {s.polished_at ? `Polish (${s.polished_turns ?? 0} fixed)` : 'Polish for publishing'}
            </button>
          )}
          {s && series?.format === 'video' && (
            <button className={btnGhost} disabled={ep.busy || dirty || layouts.isPending || !ep.brief_md} onClick={() => layouts.mutate(false)}
              title={dirty ? 'Save the script first' : 'Pick slide layouts — big numbers, a quotation, two columns — for the segments that suit them (one LLM pass; the dialogue stays, figures and quotations must be in the brief)'}>
              {layouts.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} {layoutCount ? `Slide layouts (${layoutCount})` : 'Slide layouts'}
            </button>
          )}
          {s && layoutCount > 0 && (
            <button className={btnGhost} disabled={ep.busy || dirty || layouts.isPending} onClick={() => layouts.mutate(true)} title="Back to bullets on every slide (no LLM call)">Plain bullets</button>
          )}
          {s && s.tags > 0 && (
            <button className={btnGhost} disabled={ep.busy || dirty || prosody.isPending} onClick={() => prosody.mutate(true)} title="Remove every delivery mark from the script (no LLM call)">Strip marks</button>
          )}
          {s && series?.format === 'video' && (
            <button className={btnGhost} aria-pressed={previewing} onClick={() => setPreviewing(!previewing)} title="The slide under the caret, live with a time scrubber, unsaved edits included. No render, no credits.">
              <Eye className="w-3.5 h-3.5" /> {previewing ? 'Hide preview' : 'Preview slides'}
            </button>
          )}
          {s && canRerender && (
            <button className={btnGhost} disabled={ep.busy || dirty || rerender.isPending} onClick={() => rerender.mutate()}
              title={dirty ? 'Save the script first' : 'New slides over the existing audio, no TTS: after changing layouts, titles or bullets. Changed the dialogue? Then Generate.'}>
              {rerender.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Film className="w-3.5 h-3.5" />} Re-render slides
            </button>
          )}
          {s && audio && (
            <button className={audio.state === 'current' ? btnGhost : btn} disabled={ep.busy || dirty || generate.isPending || audio.state === 'current'}
              title={dirty ? 'Save the script first' : audio.state === 'current' ? 'The audio matches the script, the voices and the mix — nothing to regenerate. A slide change needs Re-render slides.' : regenerateText(audio, series)}
              onClick={() => { if (confirm(`${regenerateText(audio, series)}\n\nThe previous audio and video of this episode are replaced.`)) generate.mutate({}) }}>
              {generate.isPending || ep.status === 'generating' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} {audio.state === 'remix' ? 'Re-mix audio' : 'Regenerate audio'}
            </button>
          )}
          {s && !audio && (
            <button className={btn} disabled={ep.busy || dirty || generate.isPending} title={dirty ? 'Save the script first' : `Voice every turn, mix, render the ${series?.format === 'video' ? 'slides video' : 'audiogram'}`}
              onClick={() => { if (!hasNative || confirm('Generate again? The previous audio/video of this episode is replaced.')) generate.mutate({}) }}>
              {generate.isPending || ep.status === 'generating' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (series?.format === 'video' ? <Video className="w-3.5 h-3.5" /> : <Headphones className="w-3.5 h-3.5" />)} Generate {series?.format || 'audio'}
            </button>
          )}
        </div>
        {s && audio && (
          <p className={`text-[11px] ${audio.state === 'current' ? 'text-ink-soft dark:text-mist/50' : 'text-amber-700 dark:text-amber-300'}`}>
            {audio.state === 'current' ? 'Audio is up to date with the script, voices and mix.' : regenerateText(audio, series)}
          </p>
        )}
        {err && <p className="text-sm text-red-600">{err.message}</p>}
        {s ? (
          <>
            <div className={previewing ? 'grid lg:grid-cols-2 gap-3 items-start' : ''}>
              {previewing && <ScriptSlides ep={ep} text={text} segment={segment} setSegment={setSegment} />}
              <textarea
                className={`${input} w-full min-w-0 h-[32rem] py-2 font-mono text-xs leading-relaxed`} value={text} onChange={(e) => setText(e.target.value)}
                onSelect={(e) => setSegment(segmentAt(e.target.value, e.target.selectionStart))} spellCheck={false} disabled={ep.busy}
              />
            </div>
            <p className="text-[11px] text-ink-soft dark:text-mist/50">
              <code># title</code> starts a segment (= one slide, one chapter), <code>- bullet</code> is on-screen text, <code>A:</code> / <code>B:</code> are the spoken turns (A = host, B = expert). The TTS engine reads literally — write "paragraf 5", not "§ 5". A slide can show more than bullets: <code>@stat 33 | label</code> (2–4 lines, big numbers), <code>@quote text | source</code>, <code>@column Title | item | item</code> (two lines, side by side).{prosodyOk ? <> Delivery marks in square brackets — <code>[inhalation]</code>, <code>[pause]</code>, <code>[sighs]</code>, <code>[hmm]</code>, <code>[ehm]</code>, <code>[clears throat]</code>, <code>[slowly]</code> — are performed by ElevenLabs v3/v4 and never reach captions; unknown tags are dropped.</> : null}
            </p>
          </>
        ) : (
          <p className="text-sm text-ink-soft dark:text-mist/60">"Write script" turns the brief into a two-host dialogue you can edit before anything is voiced.{!ep.brief_md ? ' Brief the episode first.' : ''}</p>
        )}
      </Fold>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Publish (O3): YouTube connection + private-first upload
// ---------------------------------------------------------------------------

function useYoutube() {
  return useQuery({ queryKey: ['overviews', 'youtube'], queryFn: api.youtubeStatus, staleTime: 30000 })
}

const useCapabilities = () => useQuery({ queryKey: ['overviews', 'capabilities'], queryFn: api.capabilities, staleTime: 60_000 })

// ---------------------------------------------------------------------------
// O5: the podcast feed Veralis serves itself, and the AI thumbnail.
// ---------------------------------------------------------------------------

function PodcastPublish({ ep, series, assets, publications, onChanged }) {
  const qc = useQueryClient()
  const caps = useCapabilities()
  const [meta, setMeta] = useState(() => ({ author: '', email: '', summary: '', category: 'Education', site_url: '', ...(series?.podcast ?? {}) }))
  useEffect(() => { setMeta((m) => ({ ...m, ...(series?.podcast ?? {}) })) }, [series?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({ mutationFn: () => api.patchSeries({ id: series.id, podcast: meta }), onSuccess: () => qc.invalidateQueries({ queryKey: ['overviews'] }) })
  const publish = useMutation({ mutationFn: () => api.publishToChannel({ id: ep.id, channel: 'podcast' }), onSuccess: onChanged })
  const media = assets.filter((a) => ['audio', 'video'].includes(a.kind) && a.origin !== 'audiogram').at(-1)
  const live = publications.filter((p) => p.channel === 'podcast' && p.state === 'public').at(-1)
  const feed = caps.data ? api.podcastFeedUrl(caps.data.public_base, series.id) : ''
  const field = (k, label) => (
    <label key={k} className="block space-y-1">
      <span className="text-[11px] text-ink-soft dark:text-mist/50">{label}</span>
      <input className={`${input} w-full`} value={meta[k] ?? ''} onChange={(e) => setMeta({ ...meta, [k]: e.target.value })} />
    </label>
  )
  return (
    <Fold id="podcast" head={<div className="flex items-center gap-2 text-sm text-ink dark:text-mist"><Rss className="w-4 h-4 text-gold-deep dark:text-gold" /> Podcast feed</div>}>
      <div className="flex items-center gap-2 text-[11px] text-ink-soft dark:text-mist/50">
        <code className="truncate flex-1" title={feed}>{feed}</code>
        <CopyButton text={feed} label="Copy URL" />
        {feed && <a className={btnGhost} href={feed} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5" /> Open</a>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {field('author', 'Author')}{field('email', 'Owner e-mail')}{field('category', 'Apple category')}{field('site_url', 'Site URL')}
      </div>
      <label className="block space-y-1">
        <span className="text-[11px] text-ink-soft dark:text-mist/50">Show summary</span>
        <textarea className={`${input} w-full h-16 py-2`} value={meta.summary ?? ''} onChange={(e) => setMeta({ ...meta, summary: e.target.value })} />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <button className={btnGhost} onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save show metadata</button>
        <button className={btn} disabled={!media || ep.busy || publish.isPending} onClick={() => { if (confirm(live ? 'Re-publish this episode to the feed (same entry, current file)?' : 'Add this episode to the public podcast feed?')) publish.mutate() }}>
          {publish.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rss className="w-4 h-4" />} {live ? 'Re-publish to feed' : 'Publish to feed'}
        </button>
        {!media && <span className="text-[11px] text-ink-soft dark:text-mist/50">needs an audio or video asset</span>}
        {media && <span className="text-[11px] text-ink-soft dark:text-mist/50">enclosure: {media.meta?.filename || media.path.split(/[\\/]/).pop()} ({fmtDur(media.duration_s)})</span>}
      </div>
      {live && <p className="text-[11px] text-ink-soft dark:text-mist/60">In the feed since {new Date(live.created_at).toLocaleString()} · <a className="underline" href={live.url} target="_blank" rel="noreferrer">media URL</a></p>}
      <p className="text-[11px] text-ink-soft dark:text-mist/50">
        The feed and its media are served by Veralis without login (the only public part of the overviews API): subscribe in any podcast app with the URL above, or submit it to Apple Podcasts / Spotify. Publishing also indexes the overview back into the project.
      </p>
      {(publish.isError || save.isError) && <p className="text-sm text-red-600">{publish.error?.message || save.error?.message}</p>}
    </Fold>
  )
}

// Media can exist with no prompt pack (a script written before the pack came with it);
// the image prompt and the YouTube text both live in the pack.
function ComposePack({ ep, why, onChanged }) {
  const compose = useMutation({ mutationFn: () => api.promptEpisode(ep.id), onSuccess: onChanged })
  return (
    <div className="flex flex-wrap items-center gap-2">
      <p className="flex-1 min-w-[12rem] text-[11px] text-amber-700 dark:text-amber-300">{why}</p>
      <button className={btnGhost} disabled={ep.busy || compose.isPending || !ep.brief_md} onClick={() => compose.mutate()}>
        {compose.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Compose prompts
      </button>
      {compose.isError && <p className="w-full text-sm text-red-600">{compose.error.message}</p>}
    </div>
  )
}

function ThumbnailCard({ ep, assets, onChanged }) {
  const caps = useCapabilities()
  const pp = ep.prompt_pack ?? {}
  const [prompt, setPrompt] = useState(pp.thumbnail_prompt ?? '')
  const [text, setText] = useState(pp.thumbnail_text ?? '')
  useEffect(() => { setPrompt(pp.thumbnail_prompt ?? ''); setText(pp.thumbnail_text ?? '') }, [pp.generated_at, pp.thumbnail_prompt, pp.thumbnail_text]) // eslint-disable-line react-hooks/exhaustive-deps
  const gen = useMutation({
    mutationFn: () => api.generateThumbnail({ id: ep.id, prompt: prompt.trim(), text, overlay: text.trim() ? undefined : false }),
    onSuccess: onChanged,
  })
  const thumbs = assets.filter((a) => a.kind === 'thumbnail')
  const ai = thumbs.filter((a) => a.origin === 'imagen').at(-1)
  const template = thumbs.filter((a) => a.origin === 'template').at(-1)
  const img = caps.data?.image
  if (!thumbs.length && !pp.thumbnail_prompt) return null
  return (
    <Fold id="thumbnail" head={
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-ink dark:text-mist"><ImageIcon className="w-4 h-4 text-gold-deep dark:text-gold" /> Thumbnail</div>
        <span className="text-[11px] text-ink-soft dark:text-mist/50">{ai ? 'the AI image is used when publishing' : template ? 'the template card is used when publishing' : 'none yet'}</span>
      </div>}>
      {(ai || template) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[ai, template].filter(Boolean).map((t) => (
            <figure key={t.id} className="space-y-1">
              <img src={`${api.assetFileUrl(ep.id, t.id)}#${t.created_at}`} alt={t.origin} className="w-full rounded-lg border border-parchment-border dark:border-midnight-600" />
              <figcaption className="text-[11px] text-ink-soft dark:text-mist/50 flex items-center gap-2">
                <span className="flex-1">{t.origin === 'imagen' ? `AI · ${t.meta?.model || t.meta?.provider || ''}` : 'template card'}{t.meta?.bytes ? ` · ${(t.meta.bytes / 1024).toFixed(0)} kB` : ''}</span>
                <a className="underline" href={api.assetFileUrl(ep.id, t.id, true)}>download</a>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {!pp.youtube?.title && !prompt.trim() && <ComposePack ep={ep} onChanged={onChanged} why="This episode has no prompt pack, so there is no image prompt yet. Compose it, or write your own below." />}
      <label className="block space-y-1">
        <span className="text-[11px] text-ink-soft dark:text-mist/50">Image prompt (English works best; the prompt pack's by default)</span>
        <textarea className={`${input} w-full h-20 py-2`} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="A 16:9 thumbnail: concrete scene, style, colours…" />
      </label>
      <div className="flex flex-wrap items-end gap-2">
        <label className="block space-y-1 flex-1 min-w-[12rem]">
          <span className="text-[11px] text-ink-soft dark:text-mist/50">Title overlay (blank = none)</span>
          <input className={`${input} w-full`} value={text} onChange={(e) => setText(e.target.value)} />
        </label>
        <button className={btn} disabled={!img?.configured || !prompt.trim() || ep.busy || gen.isPending} onClick={() => gen.mutate()} title={img?.configured ? `${img.provider} · ${img.model}` : img?.hint || ''}>
          {gen.isPending || ep.progress?.step === 'imagine' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {ai ? 'Regenerate AI thumbnail' : 'Generate AI thumbnail'}
        </button>
      </div>
      {img && !img.configured && <p className="text-[11px] text-amber-600">No image backend: {img.hint}</p>}
      {img?.configured && <p className="text-[11px] text-ink-soft dark:text-mist/50">{img.provider === 'vertex' ? 'Vertex AI Imagen' : 'Gemini image model (Nano Banana)'} · {img.model} · cropped to 1280×720 JPEG with the template's title band.</p>}
      {gen.isError && <p className="text-sm text-red-600">{gen.error?.message}</p>}
      {ep.last_error?.startsWith('thumbnail:') && !ep.busy && <p className="text-sm text-red-600">{ep.last_error}</p>}
    </Fold>
  )
}

function YoutubeConnection() {
  const qc = useQueryClient()
  const q = useYoutube()
  const connect = useMutation({ mutationFn: api.youtubeConnect, onSuccess: (d) => { window.location.assign(d.url) } })
  const disconnect = useMutation({ mutationFn: api.youtubeDisconnect, onSuccess: () => qc.invalidateQueries({ queryKey: ['overviews', 'youtube'] }) })
  // the OAuth round-trip lands back here with ?youtube=connected|error
  const [notice, setNotice] = useState(() => {
    const p = new URLSearchParams(window.location.search)
    const v = p.get('youtube')
    if (!v) return null
    window.history.replaceState(null, '', window.location.pathname)
    return v === 'connected' ? { ok: true } : { ok: false, reason: p.get('reason') || 'unknown' }
  })
  useEffect(() => { if (notice?.ok) qc.invalidateQueries({ queryKey: ['overviews', 'youtube'] }) }, [notice?.ok]) // eslint-disable-line react-hooks/exhaustive-deps
  const c = q.data
  return (
    <div className={`${card} p-4 space-y-2`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 text-sm text-ink dark:text-mist">
          {c?.connected ? (
            <>YouTube: connected as <strong>{c.channel}</strong>{c.meta?.subscribers != null ? <span className="text-ink-soft dark:text-mist/50"> · {c.meta.subscribers} subscribers</span> : null}</>
          ) : c?.configured ? 'YouTube: not connected' : 'YouTube: GOOGLE_OAUTH_CLIENT_ID / SECRET missing in bff/.env'}
        </div>
        {c?.connected ? (
          <button className={btnGhost} onClick={() => { if (confirm('Disconnect YouTube? Published videos stay; Veralis just loses the token.')) disconnect.mutate() }} disabled={disconnect.isPending}>Disconnect</button>
        ) : (
          <button className={btn} onClick={() => connect.mutate()} disabled={!c?.configured || connect.isPending}>
            {connect.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} Connect YouTube
          </button>
        )}
      </div>
      {notice && !notice.ok && <p className="text-sm text-red-600">Connection failed: {notice.reason}</p>}
      {notice?.ok && <p className="text-sm text-gold-deep dark:text-gold">Connected.</p>}
      {connect.isError && <p className="text-sm text-red-600">{connect.error?.message}</p>}
    </div>
  )
}

const PUB_STATE = { uploading: 'uploading', uploaded: 'uploaded (private)', scheduled: 'scheduled', public: 'public', failed: 'failed' }

// F2: where a media asset came from, and how much of the brief it carries
const ORIGIN_LABEL = { handoff: 'NotebookLM', native: 'native voices', slides: 'native slides', audiogram: 'audiogram', composed: 'composed' }
function FidelityChip({ f }) {
  if (!f || f.terms_pct == null) return null
  const tone = f.terms_pct >= 50 ? 'text-emerald-700 dark:text-emerald-300' : f.terms_pct >= 25 ? 'text-amber-700 dark:text-amber-300' : 'text-red-600 dark:text-red-400'
  const silent = f.sections_silent?.length ? `\nSections never mentioned: ${f.sections_silent.join(' · ')}` : ''
  return (
    <span className={`ml-2 text-[11px] ${tone}`} title={`${f.terms_spoken} of ${f.terms_total} key terms of the brief are spoken; ${f.silent_count} of ${f.sections_judged} sections never come up; ${f.spoken_words} spoken words vs ${f.brief_words} in the brief.${silent}`}>
      brief {f.terms_pct} %{f.silent_count ? ` · ${f.silent_count} silent` : ''}
    </span>
  )
}

function PublishPanel({ ep, series, assets, publications, onChanged }) {
  const ytq = useYoutube()
  const caps = useCapabilities()
  const [privacy, setPrivacy] = useState('private')
  useEffect(() => { if (caps.data?.defaults?.yt_privacy) setPrivacy(caps.data.defaults.yt_privacy) }, [caps.data?.defaults?.yt_privacy])
  const [publishAt, setPublishAt] = useState('')
  // F2: more than one video (NotebookLM drop + native alternative) → the owner picks
  const videos = assets.filter((a) => a.kind === 'video')
  const [assetId, setAssetId] = useState(null)
  const video = videos.find((a) => a.id === assetId) ?? videos.at(-1)
  const publish = useMutation({ mutationFn: () => api.publishEpisode({ id: ep.id, privacy, publish_at: publishAt ? new Date(publishAt).toISOString() : null, asset_id: video?.id ?? null }), onSuccess: onChanged })
  const yt = ep.prompt_pack?.youtube
  const canPublish = !!video && !!yt?.title && ytq.data?.connected && !ep.busy
  return (
    <div className="space-y-3">
      {series?.channel === 'podcast' && <PodcastPublish ep={ep} series={series} assets={assets} publications={publications} onChanged={onChanged} />}
      <YoutubeConnection />
      <Fold id="youtube-publish" defaultOpen head={<div className="text-sm text-ink dark:text-mist">Publish to YouTube</div>}>
        {!video && <p className="text-sm text-ink-soft dark:text-mist/60">No video asset yet — drop the overview file in the Asset tab first.</p>}
        {videos.length > 1 && (
          <div className="space-y-1">
            <div className="text-[11px] text-ink-soft dark:text-mist/50">Which video</div>
            {videos.map((a) => (
              <label key={a.id} className="flex items-center gap-2 text-sm text-ink dark:text-mist cursor-pointer">
                <input type="radio" name="publish-asset" checked={video?.id === a.id} onChange={() => setAssetId(a.id)} />
                <span className="truncate">{a.meta?.filename || a.path.split(/[\\/]/).pop()}</span>
                <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-gold/15 text-gold-deep dark:text-gold">{ORIGIN_LABEL[a.origin] || a.origin}</span>
                <span className="text-[11px] text-ink-soft dark:text-mist/50">{fmtDur(a.duration_s)}</span>
                {a.meta?.fidelity && <FidelityChip f={a.meta.fidelity} />}
              </label>
            ))}
          </div>
        )}
        {video && !yt?.title && <ComposePack ep={ep} onChanged={onChanged} why="No YouTube title and description yet: they come from the prompt pack." />}
        {video && (
          <div className="text-[11px] text-ink-soft dark:text-mist/50">
            Uploads <code>{video.path.split(/[\\/]/).pop()}</code> ({fmtDur(video.duration_s)}) with the title, description, tags and chapters from the prompt pack, then sets the cover as thumbnail, adds it to the series playlist and uploads the captions.
          </div>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <label className="block space-y-1">
            <span className="text-[11px] text-ink-soft dark:text-mist/50">Privacy</span>
            <select className={input} value={privacy} onChange={(e) => setPrivacy(e.target.value)} disabled={!!publishAt}>
              <option value="private">private</option>
              <option value="unlisted">unlisted</option>
              <option value="public">public</option>
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] text-ink-soft dark:text-mist/50">Schedule (optional, goes public at)</span>
            <input className={input} type="datetime-local" value={publishAt} onChange={(e) => setPublishAt(e.target.value)} />
          </label>
          <button className={btn} disabled={!canPublish || publish.isPending} onClick={() => { if (confirm(`Upload "${yt?.title}" to YouTube as ${publishAt ? 'scheduled' : privacy}?`)) publish.mutate() }}>
            {publish.isPending || ep.status === 'publishing' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} Publish
          </button>
        </div>
        <p className="text-[11px] text-ink-soft dark:text-mist/50">
          Until the Google Cloud project passes YouTube's API compliance audit, every API upload is locked to <strong>private</strong> regardless of the choice above — switch it to public in YouTube Studio, or re-publish after the audit.
        </p>
        {publish.isError && <p className="text-sm text-red-600">{publish.error?.message}</p>}
      </Fold>
      {series?.channel !== 'podcast' && <PodcastPublish ep={ep} series={series} assets={assets} publications={publications} onChanged={onChanged} />}
      {publications.length > 0 && (
        <Fold id="publications" defaultOpen body="space-y-2" head={<div className="text-[11px] uppercase tracking-[0.18em] text-gold-deep dark:text-gold">Publications ({publications.length})</div>}>
          {publications.map((p) => (
            <div key={p.id} className="text-sm space-y-0.5">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="text-[11px] uppercase tracking-[0.12em] text-ink-soft dark:text-mist/50">{p.channel}</span>
                <span className={`px-2 py-0.5 rounded-full text-[11px] ${p.state === 'failed' ? 'bg-red-500/15 text-red-600' : 'bg-gold/15 text-gold-deep dark:text-gold'}`}>{p.channel === 'podcast' && p.state === 'public' ? 'in the feed' : PUB_STATE[p.state] || p.state}</span>
                {p.url ? <a className="text-ink dark:text-mist underline truncate" href={p.url} target="_blank" rel="noreferrer">{p.url}</a> : <span className="text-ink-soft">{p.meta?.title}</span>}
                <span className="text-[11px] text-ink-soft dark:text-mist/50">{new Date(p.created_at).toLocaleString()}</span>
              </div>
              {p.privacy && p.meta?.requested_privacy && p.privacy !== p.meta.requested_privacy && (
                <div className="text-[11px] text-ink-soft dark:text-mist/60">requested {p.meta.requested_privacy}, YouTube set <strong>{p.privacy}</strong></div>
              )}
              {p.publish_at && <div className="text-[11px] text-ink-soft dark:text-mist/60">goes public {new Date(p.publish_at).toLocaleString()}</div>}
              {p.meta?.error && <div className="text-[11px] text-red-600">{p.meta.error}</div>}
              {(p.meta?.warnings ?? []).map((w, i) => <div key={i} className="text-[11px] text-amber-600">⚠ {w}</div>)}
            </div>
          ))}
        </Fold>
      )}
    </div>
  )
}

function AssetPanel({ ep, series, assets, onChanged }) {
  const [progress, setProgress] = useState(null)
  const [over, setOver] = useState(false)
  const upload = useMutation({
    mutationFn: (file) => api.uploadAsset({ id: ep.id, file, onProgress: setProgress }),
    onSuccess: () => { setProgress(null); onChanged() },
    onError: () => setProgress(null),
  })
  const reprocess = useMutation({ mutationFn: (assetId) => api.reprocessAsset({ id: ep.id, assetId }), onSuccess: onChanged })
  const compose = useMutation({ mutationFn: (assetId) => api.composeAsset({ id: ep.id, assetId }), onSuccess: onChanged })
  const pick = (files) => { const f = files?.[0]; if (f && !ep.busy) upload.mutate(f) }

  // F2: every media asset is listed (a dropped NotebookLM file, the native
  // alternative, audiograms) with its provenance and brief fidelity; the
  // preview follows the picked one
  const sources = assets.filter((a) => ['audio', 'video'].includes(a.kind))
  const [previewId, setPreviewId] = useState(null)
  const thumb = assets.filter((a) => a.kind === 'thumbnail').at(-1)
  const videos = assets.filter((a) => a.kind === 'video')
  const video = videos.find((a) => a.id === previewId) ?? videos.at(-1)
  const nativeVideo = video ? (video.origin === 'slides' || video.meta?.native === true) : false
  const captions = assets.filter((a) => a.kind === 'captions' && a.origin === (nativeVideo ? 'script' : 'transcribe')).at(-1) ?? assets.filter((a) => a.kind === 'captions').at(-1)
  const transcript = assets.filter((a) => a.kind === 'transcript' && a.origin === (nativeVideo ? 'script' : 'transcribe')).at(-1) ?? assets.filter((a) => a.kind === 'transcript').at(-1)
  const chapters = ep.prompt_pack?.youtube?.chapters ?? []

  return (
    <div className="space-y-3">
      <label
        onDragOver={(e) => { e.preventDefault(); setOver(true) }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files) }}
        className={`block rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition ${
          over ? 'border-gold bg-gold/10' : 'border-parchment-border dark:border-midnight-600'
        } ${ep.busy ? 'opacity-50 pointer-events-none' : ''}`}
      >
        <input type="file" accept="audio/*,video/*,.mp3,.m4a,.wav,.mp4,.webm,.mkv,.mov" className="hidden" onChange={(e) => { pick(e.target.files); e.target.value = '' }} />
        {upload.isPending ? (
          <span className="inline-flex items-center gap-2 text-sm text-ink-soft dark:text-mist/60">
            <Loader2 className="w-4 h-4 animate-spin" /> Uploading{progress != null ? ` ${progress}%` : '…'}
          </span>
        ) : (
          <>
            <Download className="w-6 h-6 mx-auto mb-1 opacity-50" />
            <div className="text-sm text-ink dark:text-mist">{series?.generator === 'native' ? `Or drop your own ${series?.format === 'video' ? 'video' : 'audio'} here (the Script tab generates one)` : `Drop the NotebookLM ${series?.format === 'video' ? 'video' : 'audio'} here, or click to choose`}</div>
            <div className="text-[11px] text-ink-soft dark:text-mist/50">mp3 · m4a · wav · mp4 · webm · mov — transcribed, chaptered and {series?.format === 'video' ? 'covered' : 'wrapped as an audiogram'} for YouTube</div>
          </>
        )}
      </label>
      {upload.isError && <p className="text-sm text-red-600">{upload.error?.message}</p>}
      {reprocess.isError && <p className="text-sm text-red-600">{reprocess.error?.message}</p>}

      <ThumbnailCard ep={ep} assets={assets} onChanged={onChanged} />

      {sources.length > 0 && (
        <Fold id="asset-files" defaultOpen head={<div className="text-sm text-ink dark:text-mist">Files ({sources.length})</div>}>
          {sources.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              {a.kind === 'video' ? <Video className="w-4 h-4 text-gold-deep dark:text-gold" /> : <Headphones className="w-4 h-4 text-gold-deep dark:text-gold" />}
              <span className="flex-1 min-w-0 truncate text-ink dark:text-mist" title={a.path}>
                {a.meta?.filename || a.path.split(/[\\/]/).pop()}
                <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-gold/15 text-gold-deep dark:text-gold">{ORIGIN_LABEL[a.origin] || a.origin}</span>
                {a.meta?.fidelity && <FidelityChip f={a.meta.fidelity} />}
              </span>
              <span className="text-[11px] text-ink-soft dark:text-mist/50">{fmtDur(a.duration_s)}{a.meta?.bytes ? ` · ${(a.meta.bytes / 1048576).toFixed(1)} MB` : ''} · {a.origin}{a.origin === 'native' && a.meta?.turns ? ` · ${a.meta.turns} turns · ${a.meta.provider}` : ''}</span>
              {a.kind === 'video' && videos.length > 1 && (
                <button className={video?.id === a.id ? btn : btnGhost} onClick={() => setPreviewId(a.id)} title="Show this one in the player below"><Video className="w-3.5 h-3.5" /> Preview</button>
              )}
              {a.origin === 'handoff' && (
                <button className={btnGhost} disabled={ep.busy || reprocess.isPending} onClick={() => reprocess.mutate(a.id)} title="Re-run transcript, chapters, cover and audiogram from this file">
                  <RefreshCw className="w-3.5 h-3.5" /> Re-process
                </button>
              )}
              {a.origin === 'handoff' && a.kind === 'video' && (
                <button className={btnGhost} disabled={ep.busy || compose.isPending} onClick={() => compose.mutate(a.id)} title="Compose the intro card and the spoken family outro around this file (new asset; the original stays)">
                  <Film className="w-3.5 h-3.5" /> {videos.some((v) => v.origin === 'composed' && v.meta?.source_asset_id === a.id) ? (videos.some((v) => v.origin === 'composed' && v.meta?.source_asset_id === a.id && ep.family_hash && v.meta?.family_hash !== ep.family_hash) ? 'Recompose (family changed)' : 'Recompose') : 'Compose intro/outro'}
                </button>
              )}
            </div>
          ))}
          {video && (
            <video controls preload="metadata" className="w-full rounded-lg bg-black" src={api.assetFileUrl(ep.id, video.id)} poster={thumb ? api.assetFileUrl(ep.id, thumb.id) : undefined} />
          )}
          {!video && sources[0]?.kind === 'audio' && (
            <audio controls preload="metadata" className="w-full" src={api.assetFileUrl(ep.id, sources.at(-1).id)} />
          )}
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-ink-soft dark:text-mist/50">
            {thumb && <a className={btnGhost} href={api.assetFileUrl(ep.id, thumb.id, true)}><Download className="w-3.5 h-3.5" /> cover.png</a>}
            {video && ['audiogram', 'slides'].includes(video.origin) && <a className={btnGhost} href={api.assetFileUrl(ep.id, video.id, true)}><Download className="w-3.5 h-3.5" /> {video.path.split(/[\\/]/).pop()}</a>}
            {sources.find((a) => a.origin === 'native') && <a className={btnGhost} href={api.assetFileUrl(ep.id, sources.find((a) => a.origin === 'native').id, true)}><Download className="w-3.5 h-3.5" /> episode.mp3</a>}
            {captions && <a className={btnGhost} href={api.assetFileUrl(ep.id, captions.id, true)}><Download className="w-3.5 h-3.5" /> captions.srt</a>}
            {transcript && <a className={btnGhost} href={api.assetFileUrl(ep.id, transcript.id, true)}><Download className="w-3.5 h-3.5" /> transcript.txt</a>}
          </div>
          {chapters.length > 0 && (
            <div className="text-xs text-ink-soft dark:text-mist/70">
              <div className="text-[11px] uppercase tracking-[0.18em] text-gold-deep dark:text-gold mb-1">Chapters (in the YouTube description)</div>
              {chapters.map((c, i) => <div key={i}><span className="font-mono">{fmtDur(c.start)}</span> {c.title}</div>)}
            </div>
          )}
        </Fold>
      )}
    </div>
  )
}

function PromptPack({ ep, series }) {
  const qc = useQueryClient()
  const pp = ep.prompt_pack
  const [yt, setYt] = useState(pp.youtube ?? {})
  useEffect(() => setYt(pp.youtube ?? {}), [pp.generated_at]) // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({
    mutationFn: () => api.patchEpisode({ id: ep.id, prompt_pack: { youtube: { ...yt, tags: String(yt.tags_text ?? (yt.tags ?? []).join(', ')).split(',').map((t) => t.trim()).filter(Boolean) } } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['overviews'] }),
  })
  const block = (id, title, body, hint) => (
    <Fold id={id} body="space-y-2" head={
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-ink dark:text-mist">{title}</div>
        <CopyButton text={body} />
      </div>}>
      {hint && <p className="text-[11px] text-ink-soft dark:text-mist/50">{hint}</p>}
      <pre className="whitespace-pre-wrap text-xs text-ink-soft dark:text-mist/70 font-sans">{body}</pre>
    </Fold>
  )
  const primary = series?.format === 'video' ? pp.notebooklm_video_prompt || pp.notebooklm_prompt : pp.notebooklm_prompt
  const secondary = series?.format === 'video' ? pp.notebooklm_prompt : pp.notebooklm_video_prompt
  return (
    <div className="space-y-3">
      {block('prompt-primary', `NotebookLM ${series?.format || 'audio'} overview prompt`, primary, 'Paste into Studio → Customize before generating.')}
      {secondary && block('prompt-secondary', series?.format === 'video' ? 'Audio overview prompt (alternative)' : 'Video overview prompt (alternative)', secondary)}
      {pp.thumbnail_prompt && block('prompt-thumbnail', 'Thumbnail prompt (Imagen / Nano Banana)', pp.thumbnail_prompt, `Template text: "${pp.thumbnail_text || ''}" — O3 renders the template card automatically.`)}
      <Fold id="youtube-meta" defaultOpen body="space-y-2" head={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-ink dark:text-mist">YouTube metadata</div>
          <div className="flex gap-1">
            <CopyButton text={yt.description} label="Copy description" />
            <button className={btn} onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save</button>
          </div>
        </div>}>
        <label className="block space-y-1">
          <span className="text-[11px] text-ink-soft dark:text-mist/50">Title ({(yt.title ?? '').length}/100)</span>
          <input className={`${input} w-full`} value={yt.title ?? ''} maxLength={100} onChange={(e) => setYt({ ...yt, title: e.target.value })} />
        </label>
        <label className="block space-y-1">
          <span className="text-[11px] text-ink-soft dark:text-mist/50">Description</span>
          <textarea className={`${input} w-full h-56 py-2`} value={yt.description ?? ''} onChange={(e) => setYt({ ...yt, description: e.target.value })} />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block space-y-1 md:col-span-2">
            <span className="text-[11px] text-ink-soft dark:text-mist/50">Tags (comma-separated)</span>
            <input className={`${input} w-full`} value={yt.tags_text ?? (yt.tags ?? []).join(', ')} onChange={(e) => setYt({ ...yt, tags_text: e.target.value })} />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] text-ink-soft dark:text-mist/50">Category · language · playlist</span>
            <div className="text-xs text-ink-soft dark:text-mist/70 pt-2">{yt.category} · {yt.language} · {yt.playlist_title}</div>
          </label>
        </div>
        {save.isError && <p className="text-sm text-red-600">{save.error?.message}</p>}
      </Fold>
    </div>
  )
}

function SourcePack({ ep }) {
  const pack = ep.source_pack ?? []
  const urls = pack.filter((s) => s.source_url).map((s) => s.source_url).join('\n')
  const cited = pack.filter((s) => s.cited).length
  if (!pack.length) return <p className="text-sm text-ink-soft dark:text-mist/60">The source pack is built with the brief.</p>
  return (
    <Fold id="source-pack" defaultOpen head={
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-ink-soft dark:text-mist/50">
        <span>{pack.length} sources · {cited} cited in the brief (add those first when a notebook's source limit bites)</span>
        {urls && <CopyButton text={urls} label="Copy YouTube URLs" />}
      </div>}>
      <div className="divide-y divide-parchment-border dark:divide-midnight-600">
        {pack.map((s, i) => (
          <div key={i} className="flex items-center gap-3 py-1.5 text-sm">
            <span className={`w-12 text-[10px] uppercase ${s.cited ? 'text-gold-deep dark:text-gold' : 'text-ink-soft/60 dark:text-mist/40'}`}>{s.cited ? 'cited' : ''}</span>
            <span className="flex-1 min-w-0 truncate text-ink dark:text-mist" title={s.path}>{s.title}</span>
            <span className="text-[11px] text-ink-soft dark:text-mist/50">{s.kind}{s.chunks ? ` · ${s.chunks} chunks` : ''}</span>
            {s.source_url ? (
              <a className={btnGhost} href={s.source_url} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5" /> open</a>
            ) : (
              <a className={btnGhost} href={api.sourceFileUrl(ep.id, i)}><Download className="w-3.5 h-3.5" /> file</a>
            )}
          </div>
        ))}
      </div>
    </Fold>
  )
}
